import React, { useEffect, useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import {
  XMarkIcon,
  DocumentDuplicateIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { useArtworkStore } from "../store/artworkStore";
import { useContactStore } from "../store/contactStore";
import { useCartStore } from "../store/cartStore";
import { useAuthStore } from "../store/authStore";
import {
  formatArtworkPrice,
  getArtworkPrice,
  formatPrice,
  formatPriceForPDF,
} from "../utils/currency";
// import { publicProposalBaseUrl } from "../config/env";
import { useUIStore } from "../store/uiStore";
import { useCurrencyStore, CurrencyCode } from "../store/currencyStore";
import { registerAcuminProFonts } from "../utils/pdfFonts";
import {
  cleanSanitizedText,
  normalizeWeirdSpacing,
} from "../utils/textSanitizer";
import {
  buildProposalNoteBody,
  escapeProposalNoteText,
  type ProposalNoteAction,
} from "../utils/proposalNoteHtml";
import { getPrimaryArtworkImageUrl } from "../utils/artworkImages";
import { getArtworkEditionText } from "../utils/artworkEdition";
import UserSearchSelect, { UserOption } from "./UserSearchSelect";

interface ProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialArtwork?: {
    id: string;
    title?: string;
    artist?: string;
    price?: number;
    currency?: string;
    image_url?: string | null;
    IdName?: string;
    prices?: any;
  } | null;
  /** When set, use this list instead of cart (e.g. for bulk PDF download). */
  selectedArtworksOverride?: any[] | null;
  /** When set and single-artwork PDF is generated, call this instead of saving. Used by bulk download. */
  onSinglePdfReady?: (blob: Blob, artwork: any) => void;
  /** When true and exactly one artwork (override or cart), auto-run single PDF and call onSinglePdfReady. */
  autoDownloadSingle?: boolean;
  /** After proposal actions sync artists to a contact’s interests (Odoo), notify parent to refresh local UI. */
  onContactInterestsSynced?: (info: {
    contactId: number;
    _model?: "crm.prospect" | "res.partner";
  }) => void;
  /** Offers pipeline: log this send on an existing `crm.lead` (and skip resolving lead from contact). */
  crmLeadId?: number | null;
  /** Offers pipeline: pre-fill recipient from the open opportunity card. */
  pipelineEmailPrefill?: {
    email?: string;
    partnerNumericId?: number;
    prospectNumericId?: number;
    clientName?: string;
  } | null;
  // ── List-mode props (used when the modal is opened from the Manage Lists
  //    page via the "Send" button) ───────────────────────────────────────────
  /**
   * Hide the "Add Artwork(s)" search, the "Selected artworks" carousel, the
   * top action toolbar (Web view / Copy PDF link / Download pdf), and any
   * "Suggested artworks" pill row. Use when artworks are pre-determined by
   * the caller (e.g. an art list) and the recipient should not be able to
   * change them inside the modal.
   */
  hideArtworkPicker?: boolean;
  /**
   * Hide the file picker + "Choose attachment" UI under "Attachments".
   * Combined with `prefilledAttachments` this lets the caller send a
   * controlled set of files (e.g. a single PDF generated for the chosen
   * art list) without letting the user add their own.
   */
  hideAttachmentPicker?: boolean;
  /**
   * Pre-populate `attachments` when the modal opens. Each entry follows the
   * same `ProposalEmailAttachment` shape used internally; pass
   * `source: "proposal"` for files we generated ourselves so they don't get
   * tagged as user uploads in the CRM sync step.
   */
  prefilledAttachments?: Array<{
    name: string;
    content: string;
    source?: "proposal" | "external";
  }>;
  /**
   * Pre-populate `shareUrl` (the link that gets embedded in the email body)
   * when the modal opens. The list-page send flow uses this to drop a
   * dashboard URL for internal recipients.
   */
  prefilledShareUrl?: string | null;
  /**
   * When `true`, the recipient `UserSearchSelect` also surfaces internal
   * Opera Gallery staff (`res.users`) alongside the regular contact search.
   * Default `false` so existing call sites are unaffected.
   */
  includeInternalUsers?: boolean;
  /**
   * Predicate identifying internal-staff recipients. We call this on every
   * recipient at send time (and once when the recipient changes) so the
   * modal can branch behaviour per recipient — e.g. embed a dashboard URL
   * instead of attaching a generated PDF. Default treats nothing as
   * internal so callers must opt in.
   */
  isInternalRecipient?: (args: {
    email?: string;
    recipientLabel?: string;
    recipientId?: string;
  }) => boolean;
  /**
   * URL to surface in the body when the active recipient is internal. Use
   * when you want to redirect colleagues to a dashboard page instead of
   * sending them an attached PDF. The string is dropped into `shareUrl` so
   * it lands inside the rendered email body in the same slot the existing
   * "Web view" link uses.
   */
  internalRecipientShareUrl?: string | null;
}


interface RecipientChip {
  id: string;
  label: string;
  /** Odoo id when chip was added from a contact (for prefill / logging). */
  contactRecordId?: number;
  contactModel?: "crm.prospect" | "res.partner";
}

/** `proposal` = PDFs from proposal actions; `external` = user file picker (sync these to CRM on send only). */
type ProposalEmailAttachment = {
  name: string;
  content: string;
  source: "proposal" | "external";
};

function attachmentsForOdooEmail(
  list: ProposalEmailAttachment[],
): Array<{ name: string; content: string }> {
  return list.map(({ name, content }) => ({ name, content }));
}

function base64ToBlobForCrm(base64: string, fileName: string): Blob {
  const trimmed = base64.replace(/\s/g, "");
  const binary = atob(trimmed);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const mime = fileName.toLowerCase().endsWith(".pdf")
    ? "application/pdf"
    : "application/octet-stream";
  return new Blob([bytes], { type: mime });
}

const EMPTY_CART_ITEMS: any[] = [];
type ProposalPdfTemplateLayout = "master" | "second_market" | "contemporary";

const normalizeProposalPdfTemplateLayout = (value: any): ProposalPdfTemplateLayout => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (
    normalized === "master" ||
    normalized === "second_market" ||
    normalized === "contemporary"
  ) {
    return normalized;
  }

  return "second_market";
};

export default function ProposalModal({
  isOpen,
  onClose,
  initialArtwork,
  selectedArtworksOverride,
  onSinglePdfReady,
  autoDownloadSingle,
  onContactInterestsSynced,
  crmLeadId: crmLeadIdProp,
  pipelineEmailPrefill,
  hideArtworkPicker = false,
  hideAttachmentPicker = false,
  prefilledAttachments,
  prefilledShareUrl,
  includeInternalUsers = false,
  isInternalRecipient,
  internalRecipientShareUrl,
}: ProposalModalProps) {
  const proposalPublicBase = "https://private.operagallery.com"
    // const proposalPublicBase =  window.location.origin;
  const { artworks, loadArtworks } = useArtworkStore();
  const { contacts, loadContacts } = useContactStore();
  const syncCart = useCartStore((s) => s.syncFromServer);
  const getCartItems = useCartStore((s) => s.getItems);
  const userId = useAuthStore((s) => s.user?.id);
  const userIdToItems = useCartStore((s) => s.userIdToItems);
  const { user }: any = useAuthStore();
  const hidePrices = useUIStore((s) => s.hidePrices);


  const selectedCurrency = useCurrencyStore((s) => s.selectedCurrency);

  const [recipientInput, setRecipientInput] = useState("");
  const [recipients, setRecipients] = useState<RecipientChip[]>([]);
  const [contactSuggestions, setContactSuggestions] = useState<any[]>([]);
  const [showContactSuggestions, setShowContactSuggestions] = useState(false);
  const [isSearchingContacts, setIsSearchingContacts] = useState(false);
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


  const [ccInput, setCcInput] = useState("");
  const [ccRecipients, setCcRecipients] = useState<RecipientChip[]>([]);
  const [ccContactSuggestions, setCcContactSuggestions] = useState<any[]>([]);
  const [showCcContactSuggestions, setShowCcContactSuggestions] =
    useState(false);

  const [isSearchingCcContacts, setIsSearchingCcContacts] = useState(false);
  const [ccError, setCcError] = useState<string | null>(null);

  const getRecipientName = (recipient?: RecipientChip): string => {
    if (!recipient?.label) return "Client";
    const label = String(recipient.label);
    if (label.includes(":")) return label.split(":")[0].trim();
    if (label.includes("<")) return label.split("<")[0].trim();
    if (label.includes("@")) return label.split("@")[0].trim();
    return label.trim();
  };

  const formatRecipientLabel = (
    displayName?: string | null,
    email?: string | null,
    fallbackId?: string | number,
  ): string => {
    const name = String(displayName || "").trim();
    const mail = String(email || "").trim();
    if (name && mail) return `${name} : ${mail}`;
    if (mail) return mail;
    if (name) return name;
    return fallbackId != null ? String(fallbackId) : "Client";
  };

  const addRecipientFromContact = (c: any) => {
    const displayName = c?.name || c?.display_name || c?.email || `ID ${c?.id}`;
    const emailsRaw: (string | undefined)[] = [c?.email, c?.Email1, c?.Email2];
    const emails = emailsRaw
      .map((e) => (typeof e === "string" ? e.trim() : ""))
      .filter((e) => !!e);
    const uniqueEmails = Array.from(new Set(emails));
    if (uniqueEmails.length === 0) {
      setRecipientError(
        "This contact does not have an email address. Please update the contact before sending a proposal.",
      );
      return;
    }
    const recordId = Number(c?.id);
    const contactRecordId = Number.isFinite(recordId) ? recordId : undefined;
    const contactModel = c?._model as RecipientChip["contactModel"] | undefined;

    const chips = uniqueEmails.map((em) => ({
      id: em,
      label: formatRecipientLabel(displayName, em, c?.id),
      ...(contactRecordId != null
        ? { contactRecordId, contactModel }
        : {}),
    }));


    setRecipients((prev) => {
      const newChips = chips.filter(
        (newChip) => !prev.some((existing) => existing.id === newChip.id),
      );
      return [...prev, ...newChips];
    });

    if (recipients.length === 0) {
      setGreetingName(displayName);
    }


    setRecipientInput("");
    setShowContactSuggestions(false);
    setRecipientError(null);
  };

  const [recipient, setRecipient] = useState<UserOption | null>(null);

  const [artworkQuery, setArtworkQuery] = useState("");
  const [artworkSuggestions, setArtworkSuggestions] = useState<any[]>([]);
  const [isSearchingArtworks, setIsSearchingArtworks] = useState(false);
  // const [selectedArtworks, setSelectedArtworks] = useState<any[]>([]);
  const cartItems = useMemo(() => {
    if (!userId) return EMPTY_CART_ITEMS;
    return userIdToItems[userId] || EMPTY_CART_ITEMS;
  }, [userId, userIdToItems]);
  const selectedArtworksRaw =
    selectedArtworksOverride != null ? selectedArtworksOverride : cartItems;
  // Deduplicate by id so the same artwork is not shown twice (avoids duplicate React keys and duplicate entries in UI)
  const selectedArtworks = useMemo(() => {
    const seen = new Set<string>();
    return selectedArtworksRaw.filter((a: any) => {
      const id = String(a?.id ?? "");
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [selectedArtworksRaw]);

  const [selectedTemplate, setSelectedTemplate] = useState<"1" | "2">("1");
  const [selectedTone, setSelectedTone] = useState<"1" | "2">("1");
  const [signatureTemplate, setSignatureTemplate] = useState<"sig1" | "sig2">(
    "sig1",
  );
  const [signatureProfile, setSignatureProfile] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    website: string;
    address: string;
    title?: string;
    galleryName?: string;
    zipCity?: string;
    phonePrimary?: string;
    phoneSecondary?: string;
    galleryPhone?: string;
  } | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isEditingPreview, setIsEditingPreview] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [greetingName, setGreetingName] = useState("");
  const [customGreeting, setCustomGreeting] = useState("Dear");
  // const [fullTextContent, setFullTextContent] = useState("");
  const [fullTextContent, setFullTextContent] = useState<string | null>(null);

  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [downloadDone, setDownloadDone] = useState(0);
  const [downloadTotal, setDownloadTotal] = useState(0);

  const [showPDFOptionsModal, setShowPDFOptionsModal] = useState(false);
  const [pdfModalAction, setPdfModalAction] = useState<
    "download" | "webView" | "copyPdfLink" | "view"
  >("download");
  const imageCacheRef = useRef<
    Map<
      string,
      { dataUrl: string; format: "PNG" | "JPEG"; width: number; height: number }
    >
  >(new Map());
  const yieldToUI = () => new Promise((resolve) => setTimeout(resolve, 0));
  const [generatingLink, setGeneratingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [generatingPdfLink, setGeneratingPdfLink] = useState(false);
  const [pdfLinkCopied, setPdfLinkCopied] = useState(false);
  const [messageCopied, setMessageCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [pdfShareUrl, setPdfShareUrl] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [sentFlag, setSentFlag] = useState(false);
  const [attachments, setAttachments] = useState<ProposalEmailAttachment[]>(
    [],
  );
  const PROPOSAL_MODAL_HEIGHT = 800; // px
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const messageBoxHeight = useMemo(
    () => (attachments.length > 1 && attachmentsExpanded ? 150 : 250),
    [attachments.length, attachmentsExpanded],
  );
  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  const programmaticUpdateRef = useRef(false);

  const setCaretToEnd = (element: HTMLTextAreaElement | null) => {
    if (!element) return;
    element.focus();
    element.setSelectionRange(element.value.length, element.value.length);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const fullText = e.target.value;
    setFullTextContent(fullText);


    // Extract message and link from the full text
    const linkPattern = /(https?:\/\/[^\s]+)/;
    const linkMatch = fullText.match(linkPattern);


    if (linkMatch) {
      const linkIndex = fullText.indexOf(linkMatch[0]);
      const messagePart = fullText.substring(0, linkIndex).trim();
      const linkPart = fullText.substring(linkIndex).trim();

      setMessageContent(messagePart);
      setShareUrl(linkPart);
    } else {
      setMessageContent(fullText);
      setShareUrl("");
    }
  };

  // Build a professional-looking HTML email with simple, safe inline styles
  const buildEmailHtml = (
    _recipientName: string,
    message: string,
    shareUrl?: string | null,
    _greeting?: string,
    signatureHtml?: string,
  ): string => {
    const baseText =
      // fullTextContent && fullTextContent.trim().length > 0
      //   ? fullTextContent
      //   : [
      fullTextContent !== null
        ? fullTextContent
        : [
            `${customGreeting || "Dear"} ${_recipientName || "Client"},`,
            "",
            message,

            shareUrl && !message.includes(String(shareUrl))
              ? String(shareUrl)
              : "",
          ]
            .filter(Boolean)
            .join("\n\n");

    const htmlParagraphs = baseText
      .split(/\n\s*\n/g)
      .map(
        (p) =>
          `<p style="margin:0 0 16px; color:#111827; line-height:1.6;">${p.replace(
            /\n/g,
            "<br/>",
          )}</p>`,
      )
      .join("");

    return `
<div style="background:#f8fafc; padding:24px;">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" width="100%" style="max-width:680px; background:#ffffff; border:1px solid #e5e7eb; border-radius:10px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, 'Apple Color Emoji', 'Segoe UI Emoji';">
    <tr>
      <td style="padding:24px 28px 8px 28px;">
        <div style="font-size:18px; font-weight:600; letter-spacing:.3px; color:#0f172a;">Art Gallery</div>
      </td>
    </tr>
    <tr>
      <td style="padding:0 28px 8px 28px;">
        <hr style="border:none; border-top:1px solid #e5e7eb; margin:8px 0 0 0;"/>
      </td>
    </tr>
    <tr>
      <td style="padding:20px 28px 28px 28px; font-size:15px; color:#111827;">
        ${htmlParagraphs}
        ${
          signatureHtml
            ? `<div style=\"margin-top:24px;\">${signatureHtml}</div>`
            : ""
        }
      </td>
    </tr>
  </table>

</div>`;
  };

  const copyToClipboardSafe = async (text: string) => {
    try {
      if (navigator.clipboard && (window as any).isSecureContext) {

        await navigator.clipboard.writeText(text);
        return true;

      }
    } catch (_) {
      /* fall through to fallback */
    }

    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      textArea.style.pointerEvents = "none";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textArea);
      return ok;
    } catch (_) {
      return false;
    }
  };

  const withConcurrency = async <T,>(
    tasks: Array<() => Promise<T>>,
    limit: number,
  ): Promise<T[]> => {
    const results: T[] = [];
    let idx = 0;
    const runners = new Array(Math.min(limit, tasks.length))
      .fill(0)
      .map(async () => {

        while (idx < tasks.length) {
          const current = idx++;
          results[current] = await tasks[current]();
        }
      });

    await Promise.all(runners);
    return results;
  };

  // Keep index within bounds when list changes
  useEffect(() => {
    const max = Math.max(0, selectedArtworks.length - 3);
    setCarouselIndex((i) => Math.min(i, max));
  }, [selectedArtworks.length]);

  useEffect(() => {
    if (!isOpen) return;
    // Ensure we have some data to search from
    loadContacts?.();
    loadArtworks?.(1, 50);

    // Load signature profile from Odoo for the current user
    (async () => {
      try {
        const { default: odooClient } = await import("../services/odooClient");

        const profile: any = await odooClient.getCurrentUserProfile();

        if (profile) {
          // Gallery name is already clean from API (e.g., "Paris", "London")
          const cleanGalleryName = profile.galleryName || "";
          const galleryName = cleanGalleryName
            ? `ART GALLERY - ${cleanGalleryName}`
            : "ART GALLERY";

          setSignatureProfile({
            firstName: profile.firstName,
            lastName: profile.lastName,
            email: profile.email || "",
            phone: profile.phone || "",
            website: profile.website || "",
            address: profile.address || "",
            title: profile.title || "",
            phonePrimary: profile.phonePrimary || "",
            phoneSecondary: profile.phoneSecondary || "",
            galleryName: galleryName,
            galleryPhone: profile.galleryPhone || "",
          });
        }
      } catch (_) {
        /* ignore */
      }
    })();

    // 1) Prefill instantly from current cart (no await)

    // 2) Then reconcile with server after syncing cart (background)
    (async () => {
      try {
        await syncCart();
      } catch (_) {}
      const refreshed = getCartItems?.() || [];
      if (refreshed.length === 0 && !initialArtwork) return;
      // setSelectedArtworks((prev) => {
      //   const map = new Map<string, any>(prev.map((a) => [a.id, a]));
      //   refreshed.forEach((it) => {
      //     map.set(it.id, {
      //       id: it.id,
      //       title: it.title,
      //       artist: it.artist,
      //       price: it.price,
      //       currency: it.currency,
      //       image_url: it.image_url ?? null,
      //       IdName: it.IdName,
      //       prices: it.prices,
      //     });
      //   });
      //   if (initialArtwork) {
      //     map.set(initialArtwork.id, {
      //       id: initialArtwork.id,
      //       title: initialArtwork.title || "Untitled",
      //       artist: initialArtwork.artist || "Unknown Artist",
      //       price: initialArtwork.price || 0,
      //       currency: initialArtwork.currency || "EUR",
      //       image_url: initialArtwork.image_url ?? null,
      //       IdName: initialArtwork.IdName || initialArtwork.id,
      //        prices: initialArtwork.prices,
      //     });
      //   }
      //   return Array.from(map.values());
      // });
    })();
  }, [isOpen]); // intentional deps: using stable store actions

  useEffect(() => {
    if (!isOpen) return;

    // Reset proposal send-related state
    setIsSending(false);
    setSentFlag(false);
    setGeneratingLink(false);
    setLinkCopied(false);
    setGeneratingPdfLink(false);
    setPdfLinkCopied(false);
    setMessageCopied(false);

    // 🔥 THIS IS THE REAL FIX
    setShareUrl(null);
    setPdfShareUrl(null);
    setAttachments([]);

    // Optional but safe
    setIsPdfReady(false);
  }, [isOpen]);

  // List-mode prefill — fully recipient-driven so the attachment / link
  // mirrors the routing the send loop will take. We deliberately do NOT
  // populate anything when the modal first opens: until the user picks a
  // recipient we can't know whether they'll get a PDF (external) or a
  // dashboard URL (internal), and pre-attaching the PDF only to strip it
  // again would confuse the UI ("attachment shown then disappears").
  //
  // When `prefilledShareUrl` is set without `prefilledAttachments` — i.e.
  // a pure link prefill, used by non-list callers — we still seed it on
  // open since there's no recipient-dependent branching to wait for.
  useEffect(() => {
    if (!isOpen) return;
    if (
      (!prefilledAttachments || prefilledAttachments.length === 0) &&
      prefilledShareUrl &&
      prefilledShareUrl.trim()
    ) {
      setShareUrl(prefilledShareUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Spinner shown while the recipient-driven routing effect below is
  // mid-flight. Kept as a ref-flavoured state (set true on entry, false in
  // a microtask after we've applied the new attachment/link state) so the
  // Send button stays disabled long enough for the user to see "Preparing…".
  const [listModePreparing, setListModePreparing] = useState(false);

  // Recipient-driven routing for list-mode. Whenever the recipient changes
  // (or is cleared), decide what the email needs to carry:
  //   - Internal recipient → embed the dashboard URL in the body, drop the
  //     PDF attachment so colleagues just see the in-app link.
  //   - External recipient → attach the pre-generated list PDF, drop any
  //     internal share URL so the body stays clean.
  // Skipped entirely when the caller didn't run in list-mode (no prefill
  // payload), so legacy contact-page / artwork-detail flows are unchanged.
  useEffect(() => {
    if (!isOpen) return;
    const isListMode =
      Array.isArray(prefilledAttachments) &&
      prefilledAttachments.length > 0;
    if (!isListMode) return;

    if (!recipient) {
      // Recipient cleared — wipe any auto-set state so we don't ship
      // stale attachments/link if the user re-picks differently.
      setAttachments([]);
      setShareUrl(null);
      setListModePreparing(false);
      return;
    }

    setListModePreparing(true);

    const isInternal =
      isInternalRecipient?.({
        email: recipient.email,
        recipientLabel: recipient.name,
        recipientId: String(recipient.id),
      }) ?? false;

    if (isInternal) {
      setAttachments([]);
      setShareUrl(internalRecipientShareUrl || null);
    } else {
      setShareUrl(null);
      setAttachments(
        (prefilledAttachments || []).map((a) => ({
          name: a.name,
          content: a.content,
          // We deliberately tag list-mode prefill as "external" so the
          // existing post-send loop also uploads the PDF to the resolved
          // crm.lead's chatter (the loop only picks up `source: 'external'`
          // entries). Without this, the email goes out fine but the Offers
          // page card has no attachment surfaced — users see the email but
          // not the artefact in the CRM.
          source: a.source ?? "external",
        })),
      );
    }

    // Tiny delay before flipping the spinner off — the state writes above
    // are synchronous so the UX wouldn't otherwise have a chance to render
    // the "Preparing…" affordance. 250ms feels deliberate without being
    // janky on rapid recipient swaps.
    const t = setTimeout(() => setListModePreparing(false), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, recipient]);

  // When opening, default to Template 1 content
  // useEffect(() => {
  //   if (!isOpen) return;
  //   setSelectedTemplate("1");
  //   setMessageContent(generateTemplateMessageById("1"));
  //   // keep caret position stable in editor if open
  //   setTimeout(() => {
  //     if (isEditingPreview && editorRef.current) {
  //       setCaretToEnd(editorRef.current);
  //     }
  //   }, 0);
  // }, [isOpen]); // generateTemplateMessageById is stable by design

  useEffect(() => {
  if (!isOpen) return;

  setSelectedTemplate("1");
  setMessageContent(generateTemplateMessageById("1"));
  setFullTextContent(null); // ✅ CRITICAL FIX

  setTimeout(() => {
    if (isEditingPreview && editorRef.current) {
      setCaretToEnd(editorRef.current);
    }
  }, 0);
}, [isOpen]);

  const [isPdfReady, setIsPdfReady] = useState(false);

  // -----------------Auto Attch------------------

  // const hasAutoAttachedRef = useRef(false);
  // useEffect(() => {
  //   if (!isOpen) return;
  //   if (hasAutoAttachedRef.current) return;
  //   setIsPdfReady(false);
  //   const timer = setTimeout(() => {
  //     hasAutoAttachedRef.current = true;
  //     autoAttachProposalPDF();
  //   }, 1000);
  //   return () => clearTimeout(timer);
  // }, [isOpen, selectedArtworks.length]);

  // useEffect(() => {
  //   if (!isOpen) hasAutoAttachedRef.current = false;
  // }, [isOpen]);


  const buildSignatureBlock = (
    tpl: "sig1" | "sig2",
    data: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      website?: string;
      address?: string;

      title?: string;
      galleryName?: string;
      zipCity?: string;
      phonePrimary?: string;
      phoneSecondary?: string;
    } | null,
  ): string => {
    const fullName =
      `${data?.firstName || ""} ${data?.lastName || ""}`.trim() ||
      "First Name Last Name";
    const title = data?.title ? data.title.trim() : "";
    const galleryName = data?.galleryName || "ART GALLERY";
    const addressLine = data?.address || "";
    const zipCity = data?.zipCity || "";
    const phonePrimary = data?.phonePrimary || "";
    const phoneSecondary = data?.phoneSecondary || "";
    const website = data?.website || "";
    const websiteDisplay = website
      .replace(/^https?:\/\//i, "")
      .replace(/\/$/, "");

    const phoneSegments: string[] = [];
    if (phonePrimary) phoneSegments.push(`P: ${phonePrimary}`);
    if (phoneSecondary) phoneSegments.push(`M: ${phoneSecondary}`);
    const phoneLine = phoneSegments.length
      ? `<div style="color:#111827;">${phoneSegments.join(" | ")}</div>`
      : "";

    return `

  <div style="font-family:Arial, sans-serif; font-size:13px; color:#111827; line-height:1.6;">
    <div style="font-size:14px; color:#111827;">
      ${fullName}
    </div>
    ${
      title
        ? `<div style="font-size:13px; color:#111827; margin:2px 0;">${title}</div>`
        : ""
    }
    <div style="font-weight:700; letter-spacing:3px; margin:2px 0; text-transform:uppercase; color:#111827;">
      ${galleryName}
    </div>

    ${addressLine ? `<div style="color:#111827;">${addressLine}</div>` : ""}

    ${zipCity ? `<div style="color:#111827;">${zipCity}</div>` : ""}
    ${phoneLine}
    ${
      website
        ? `<div>
      <a href="${website}"
         target="_blank"
         style="color:#111827; text-decoration:none;">
         ${websiteDisplay}
      </a>
    </div>`
        : ""
    }
  </div>`;

  };


  const signatureHtml = useMemo(
    () => buildSignatureBlock(signatureTemplate, signatureProfile),
    [signatureTemplate, signatureProfile],
  );

  const sharedProposalSeller = useMemo(() => {
    const pickFirst = (...values: any[]) => {
      for (const value of values) {
        const text = String(value || "").trim();
        if (text) return text;
      }
      return "";
    };
    const name = pickFirst(
      `${signatureProfile?.firstName || ""} ${signatureProfile?.lastName || ""}`,
      user?.display_name,
      user?.name,
      "Art Gallery",
    );
    const phone = pickFirst(
      signatureProfile?.phone,
      signatureProfile?.phonePrimary,
      signatureProfile?.phoneSecondary,
      user?.phone,
      user?.phonePrimary,
      user?.phoneSecondary,
      user?.mobilePhone,
      user?.personnelPhone,
      user?.galleryPhone,
    );
    const email = pickFirst(
      signatureProfile?.email,
      user?.email,
      user?.userEmail,
      user?.personalEmail,
      user?.login,
    );

    return {
      name,
      phone,
      phonePrimary: pickFirst(signatureProfile?.phonePrimary, user?.phonePrimary, user?.personnelPhone),
      phoneSecondary: pickFirst(signatureProfile?.phoneSecondary, user?.phoneSecondary, user?.mobilePhone),
      galleryPhone: pickFirst(signatureProfile?.galleryPhone, user?.galleryPhone),
      email,
    };
  }, [
    signatureProfile?.galleryPhone,
    signatureProfile?.email,
    signatureProfile?.firstName,
    signatureProfile?.lastName,
    signatureProfile?.phone,
    signatureProfile?.phonePrimary,
    signatureProfile?.phoneSecondary,
    user?.display_name,
    user?.email,
    user?.galleryPhone,
    user?.login,
    user?.mobilePhone,
    user?.name,
    user?.personalEmail,
    user?.personnelPhone,
    user?.phone,
    user?.phonePrimary,
    user?.phoneSecondary,
    user?.userEmail,
  ]);

  // const signatureHtml = useMemo(
  //   () =>
  //     buildSignatureBlock({
  //       firstName: signatureProfile?.firstName,
  //       lastName: signatureProfile?.lastName,
  //       title: signatureProfile?.title,
  //       phone: signatureProfile?.phone,
  //       galleryAddress: signatureProfile?.galleryAddress,
  //       zipCity: signatureProfile?.zipCity,
  //       galleryPhone: signatureProfile?.galleryPhone,
  //     }),
  //   [signatureProfile]
  // );


  const renderedArtistsRef = useRef<Set<string>>(new Set());
  const buildEmailSubject = (): string => {
    try {
      if (selectedArtworks.length <= 0) return "Artwork from Art Gallery";
      if (selectedArtworks.length === 1) {
        const a = selectedArtworks[0] || ({} as any);
        const artist = (a.artist || "").toString().trim();
        const title = (a.title || "Artwork").toString().trim();
        return artist
          ? `Artwork from Art Gallery: ${artist} — ${title}`
          : `Artwork from Art Gallery: ${title}`;
      }

      const count = selectedArtworks.length;

      const artists = Array.from(
        new Set(
          (selectedArtworks || [])
            .map((a) => (a?.artist || "").toString().trim())
            .filter(Boolean),
        ),
      );
      if (artists.length === 1) {
        return `Artworks from Art Gallery: ${artists[0]} (${count} works)`;
      }
      return `Artworks from Art Gallery (${count} works)`;
    } catch {
      return "Artworks from Art Gallery";
    }
  };

  // Contact suggestions for "Send to"
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const q = recipientInput.trim();
      if (!q) {
        setContactSuggestions([]);
        setShowContactSuggestions(false);
        return;
      }
      setIsSearchingContacts(true);
      try {
        const { default: odooClient } = await import("../services/odooClient");
        const results = await odooClient.searchContacts(q, 12);
        if (!cancelled) {
          setContactSuggestions(results || []);
          setShowContactSuggestions(true);
        }
      } catch (_) {
        if (!cancelled) setContactSuggestions([]);
      } finally {
        if (!cancelled) setIsSearchingContacts(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [recipientInput, contacts]);


  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const q = ccInput.trim();
      if (!q) {
        setCcContactSuggestions([]);
        setShowCcContactSuggestions(false);
        return;
      }
      setIsSearchingCcContacts(true);
      try {
        const { default: odooClient } = await import("../services/odooClient");
        const results = await odooClient.searchContacts(q, 12);
        if (!cancelled) {
          setCcContactSuggestions(results || []);
          setShowCcContactSuggestions(true);
        }
      } catch (_) {
        if (!cancelled) setCcContactSuggestions([]);
      } finally {
        if (!cancelled) setIsSearchingCcContacts(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [ccInput, contacts]);

  const filteredArtworkResults = useMemo(() => {
    const q = artworkQuery.trim().toLowerCase();
    if (!q) return [] as any[];
    return (artworks || [])
      .filter(
        (a) =>
          (a.title || "").toLowerCase().includes(q) ||
          (a.artist || "").toLowerCase().includes(q) ||
          String(a.IdName || a.id)
            .toLowerCase()
            .includes(q),
      )
      .slice(0, 8);
  }, [artworkQuery, artworks]);


  useEffect(() => {
    const q = artworkQuery.trim();
    if (q.length < 2) {
      setArtworkSuggestions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsSearchingArtworks(true);
      try {
        const { default: odooClient } = await import("../services/odooClient");
        const results = await odooClient.searchArtworks(q);
        if (cancelled) return;
        const pickImage = (art: any): string | null => {
          const url = getPrimaryArtworkImageUrl(art);
          if (url) return url;
          if (
            art.image_1920 &&
            art.image_1920 !== "false" &&
            art.image_1920 !== false
          )
            return `data:image/png;base64,${art.image_1920}`;
          return null;
        };
        const mapped = (results || []).slice(0, 12).map((art: any) => ({
          id: String(art.id),
          title: art.name || "Untitled",
          artist: Array.isArray(art.res_artist_id)
            ? art.res_artist_id[1] || "Unknown Artist"
            : art.res_artist_id || "Unknown Artist",
          IdName: art.IdName || undefined,
          price: (art as any).PriceRef ?? art.artwork_price_combined ?? 0,
          currency: (art as any).CurrencyRef || "EUR",
          image_url: pickImage(art),
        }));
        setArtworkSuggestions(mapped);
      } catch (_) {
        if (!cancelled) setArtworkSuggestions([]);
      } finally {
        if (!cancelled) setIsSearchingArtworks(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [artworkQuery]);

  const sameArtistSuggestions = useMemo(() => {
    if (selectedArtworks.length === 0) return [] as any[];
    const artistName = selectedArtworks[0].artist || "";
    return (artworks || []).filter(
      (a) =>
        a.artist === artistName &&
        !selectedArtworks.find((sa) => sa.id === a.id),
    );
  }, [selectedArtworks, artworks]);

  const handleAddRecipient = async (providedValue?: string) => {
    const rawValue = providedValue ?? recipientInput;
    const value = rawValue.trim();
    if (!value) {
      if (!providedValue) {
        setRecipientError("Please enter an email address or contact name.");
      }
      return;
    }

    if (emailRegex.test(value)) {
      let didAdd = false;
      const shouldSetGreeting = recipients.length === 0;
      setRecipients((prev) => {
        if (prev.some((r) => r.id === value)) return prev;
        didAdd = true;
        return [...prev, { id: value, label: value }];
      });
      if (didAdd && shouldSetGreeting) {
        setGreetingName(value.split("@")[0]);
      }
      if (didAdd) {
        setRecipientError(null);
      }
      setRecipientInput("");

      setShowContactSuggestions(false);

      return;
    }

    if (providedValue) {

      setRecipientError(
        "An email address is required to send a proposal. Please add a valid email.",
      );

      return;
    }

    setRecipientError(null);

    // Helper to pick email by priority
    const pickEmail = (c: any) => c?.email || c?.Email1 || c?.Email2 || "";

    // 1) Try to resolve against current suggestions
    if (Array.isArray(contactSuggestions) && contactSuggestions.length > 0) {
      const match =
        contactSuggestions.find((c: any) => {
          const dn = (c?.name || c?.display_name || "")
            .toString()
            .toLowerCase();

          return dn.includes(value.toLowerCase());
        }) || contactSuggestions[0];

      const email = pickEmail(match);
      if (email) {
        addRecipientFromContact(match);
        return;
      }
    }

    // 2) Fallback: query backend for a contact with an email
    try {
      const { default: odooClient } = await import("../services/odooClient");
      const results = await odooClient.searchContacts(value, 5);
      const withEmail = Array.isArray(results)
        ? results.find((c: any) => !!pickEmail(c))
        : null;
      if (withEmail) {
        addRecipientFromContact(withEmail);
        return;
      }
    } catch (_) {
      /* ignore */
    }

    setRecipientError(
      "An email address is required to send a proposal. Please add a valid email.",
    );
  };

  useEffect(() => {
    if (!showPDFOptionsModal) return;
    setIncludePrice(undefined);
    setPdfMode(null);
    const chipWithContact = recipients.find(
      (r) =>
        r.contactRecordId != null && Number.isFinite(Number(r.contactRecordId)),
    );
    if (chipWithContact?.contactRecordId != null) {
      setProposalPdfClient({
        id: chipWithContact.contactRecordId,
        name: getRecipientName(chipWithContact),
        _model: chipWithContact.contactModel,
      });
    } else {
      setProposalPdfClient(null);
    }
    // Intentionally only when the options modal opens — do not reset on recipient edits while open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPDFOptionsModal]);

  const handleRecipientPaste = async (
    event: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    const text = event.clipboardData.getData("text");
    if (!text) return;

    if (!/[;\s,]/.test(text)) {
      return;
    }

    event.preventDefault();

    const parts = text
      .split(/[;\s,]+/)
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length === 0) return;

    let hadInvalid = false;
    for (const part of parts) {
      if (emailRegex.test(part)) {
        await handleAddRecipient(part);
      } else {
        hadInvalid = true;
      }
    }

    if (hadInvalid) {
      setRecipientError(
        "An email address is required to send a proposal. Please add a valid email.",
      );
    }
  };


  const addCcRecipientFromContact = (c: any) => {
    const displayName = c?.name || c?.display_name || c?.email || `ID ${c?.id}`;
    const emailsRaw: (string | undefined)[] = [c?.email, c?.Email1, c?.Email2];
    const emails = emailsRaw
      .map((e) => (typeof e === "string" ? e.trim() : ""))
      .filter((e) => !!e);
    const uniqueEmails = Array.from(new Set(emails));
    if (uniqueEmails.length === 0) {
      setCcError(
        "This contact does not have an email address. Please update the contact before adding them to CC.",
      );
      return;
    }
    const chips = uniqueEmails.map((em) => ({
      id: em,
      label: formatRecipientLabel(displayName, em, c?.id),
    }));
    setCcRecipients((prev) => [...prev, ...chips]);
    setCcError(null);
    setCcInput("");
    setShowCcContactSuggestions(false);
  };

  // Handle adding CC recipient
  const handleAddCcRecipient = async (providedValue?: string) => {
    const rawValue = providedValue ?? ccInput;
    const value = rawValue.trim();
    if (!value) {
      if (!providedValue) {
        setCcError("Please enter an email address or contact name.");
      }
      return;
    }

    if (emailRegex.test(value)) {
      let didAdd = false;
      setCcRecipients((prev) => {
        if (prev.some((r) => r.id === value)) return prev;
        didAdd = true;
        return [...prev, { id: value, label: value }];
      });
      if (didAdd) {
        setCcError(null);
      }
      setCcInput("");
      setShowCcContactSuggestions(false);
      return;
    }

    if (providedValue) {

      setCcError(
        "An email address is required for CC. Please add a valid email.",
      );

      return;
    }

    setCcError(null);

    // Helper to pick email by priority
    const pickEmail = (c: any) => c?.email || c?.Email1 || c?.Email2 || "";

    // 1) Try to resolve against current suggestions

    if (
      Array.isArray(ccContactSuggestions) &&
      ccContactSuggestions.length > 0
    ) {

      const match =
        ccContactSuggestions.find((c: any) => {
          const dn = (c?.name || c?.display_name || "")
            .toString()
            .toLowerCase();
          return dn.includes(value.toLowerCase());
        }) || ccContactSuggestions[0];
      const email = pickEmail(match);
      if (email) {
        addCcRecipientFromContact(match);
        return;
      }
    }

    // 2) Fallback: query backend for a contact with an email
    try {
      const { default: odooClient } = await import("../services/odooClient");
      const results = await odooClient.searchContacts(value, 5);
      const withEmail = Array.isArray(results)
        ? results.find((c: any) => !!pickEmail(c))
        : null;
      if (withEmail) {
        addCcRecipientFromContact(withEmail);
        return;
      }
    } catch (_) {
      /* ignore */
    }

    setCcError(
      "An email address is required for CC. Please add a valid email.",
    );
  };

  const handleCcPaste = async (
    event: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    const text = event.clipboardData.getData("text");
    if (!text) return;

    if (!/[;\s,]/.test(text)) {
      return;
    }

    event.preventDefault();

    const parts = text
      .split(/[;\s,]+/)
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length === 0) return;

    let hadInvalid = false;
    for (const part of parts) {
      if (emailRegex.test(part)) {
        await handleAddCcRecipient(part);
      } else {
        hadInvalid = true;
      }
    }

    if (hadInvalid) {
      setCcError(
        "An email address is required for CC. Please add a valid email.",
      );
    }
  };
  const removeFromCart = useCartStore((s) => s.removeFromCart);
  const addToCart = useCartStore((s) => s.addToCart);

  useEffect(() => {
    if ( isOpen && initialArtwork) {
      addToCart(initialArtwork as any);
    }
  }, [initialArtwork]);

  // const handleAddArtwork = (a: any) => {
  //   setSelectedArtworks((prev) =>
  //     prev.find((x) => x.id === a.id) ? prev : [...prev, a]
  //   );
  //   setArtworkQuery("");
  // };

  const handleAddArtwork = async (a: any) => {
    await addToCart({
      id: a.id,
      IdName: a.IdName,
      title: a.title,
      artist: a.artist,
      price: a.price || 0,
      currency: a.currency || "EUR",
      prices: a.prices,
      image_url: a.image_url,
      status: a.status,
    });

    setArtworkQuery("");
  };

  const handleRemoveArtwork = (id: string) => {
    removeFromCart(id);
  };


  const totalAmount = useMemo(() => {
    return selectedArtworks.reduce((sum, a) => sum + (a.price || 0), 0);
  }, [selectedArtworks]);

  // kept for reference; currently unused
  // const generateMessage = () => {
  //   const names = selectedArtworks.map(a => a.title).join(', ');
  //   const amount = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totalAmount);
  //   const base = `It is a pleasure to hear from you again and we thank you for your enquiry with Art Gallery regarding ${names}.\n\nThis work is currently available with an asking price of: ${amount}. Kindly find enclosed the full information and high-resolution images of the work.`;
  //   return base;
  // };

  // Generate message body based on selected template (excludes the "Dear ...," greeting which is rendered separately)

  const generateTemplateMessageById = (tpl: "1" | "2") => {
    // const today = new Date().toLocaleDateString();

    const artworksArray = (selectedArtworks || [])
      .map((a) => {
        const title = a?.title || "Artwork";
        const year = (a as any)?.year ? `, ${(a as any).year}` : "";
        const artist = a?.artist || "Artist";
        return `""${title}${year} by ${artist}""`;
      });
    
    const artworksList = artworksArray.length > 1
      ? artworksArray.slice(0, -1).join(", ") + " and " + artworksArray[artworksArray.length - 1]
      : artworksArray.join("");

    const totalsByCurrency: Record<string, number> = {};
    (selectedArtworks || []).forEach((a) => {
      const code = String(a?.currency || "EUR");
      const price = Number(a?.price || 0);
      if (!Number.isFinite(price)) return;
      totalsByCurrency[code] = (totalsByCurrency[code] || 0) + price;
    });

    const totalPriceText = Object.entries(totalsByCurrency)
      .map(([code, sum]) => {
        try {
          return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: code,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(sum);

        } catch {
          return `${Math.round(sum).toLocaleString()} ${code}`;
        }

      })
      .join(" + ");

    const senderName = [user?.firstName, user?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const senderPosition = user?.role
      ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
      : "";

    if (tpl === "1") {
      // TEMPLATE 1 - Prospect
      return (
        `Thank you for your enquiry with Art Gallery regarding the following artwork(s): ${artworksList}.

` +

        `Kindly find enclosed the full information and high-resolution images of the works for your review.

` +
        `Please do let me know if you would like to receive further details about these works or if you would like to arrange a viewing. I would be more than happy to assist you with any questions you may have about the works, or to schedule a private appointment at your convenience.


` +
        `To give you some background information on Art Gallery, we are a Modern and Contemporary art gallery with sixteen locations globally, including New York, London, Paris, Geneva and Hong Kong. Across our exhibition programme and artwork inventory, we offer the opportunity to engage with and collect important artworks by contemporary artists and modern masters — we pride ourselves on providing a platform for connecting collectors and enthusiasts with exceptional works of art. We offer a range of bespoke services including art advisory, acquisition, collection management, logistics, exhibitions and events.

` +
        `I look forward to speaking with you and hope to have the opportunity to introduce you in person to these captivating works.

` +
        `Yours sincerely,

${senderName}`
        // ${senderPosition}`
      );
    }

    // TEMPLATE 2 - Client
    return (
      `Thank you for your new enquiry with Art Gallery regarding the following artwork(s): ${artworksList}.

` +

      `Kindly find enclosed the full information and high-resolution images of the works for your review.


` +
      `I remain at your disposal should you like to arrange a viewing, and rest assured that I would be more than happy to assist you and answer with any questions that you may have about the work or the price.

` +
      `I look forward to hearing from you and remain 

` +
      `Yours sincerely,

${senderName}`
      // ${senderPosition}`
    );
  };

  const extractEmailFromLabel = (label: string): string | null => {
    const parts = label.split(":");
    if (parts.length >= 2) {
      const email = parts.slice(1).join(":").trim();
      return email || null;
    }
    const m = label.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    return m ? m[0] : null;
  };
  
  useEffect(() => {
    if (!isOpen) return;

    setMessageContent(generateTemplateMessageById(selectedTemplate));
  }, [isOpen, selectedArtworks, totalAmount, selectedTemplate]); // generateTemplateMessageById intentionally not included





  const [includePrice, setIncludePrice] = useState<boolean | null | undefined>(
    null,
  );
  const [pdfMode, setPdfMode] = useState<"single" | "multiple" | null>(null);

  const [proposalPdfClient, setProposalPdfClient] = useState<UserOption | null>(
    null,
  );

  useEffect(() => {
    setRecipients(recipient ? [{
      id: String(recipient.id),
      label: formatRecipientLabel(recipient.name, recipient.email, recipient.id),
      contactRecordId: Number.isFinite(recipient.id) ? Number(recipient.id) : undefined,
      contactModel: recipient._model,
    }] : []);
  }, [recipient]);

  const pipelineEmailPrefillAppliedRef = useRef(false);


  useEffect(() => {
    if (!isOpen) {
      pipelineEmailPrefillAppliedRef.current = false;
      return;
    }
    if (!pipelineEmailPrefill) return;
    if (pipelineEmailPrefillAppliedRef.current) return;
    pipelineEmailPrefillAppliedRef.current = true;
    const p = pipelineEmailPrefill;
    const name = (p.clientName || "").trim() || "Client";
    const em = (p.email || "").trim();
    if (
      p.prospectNumericId != null &&
      Number.isFinite(Number(p.prospectNumericId))
    ) {
      setRecipient({
        id: Number(p.prospectNumericId),
        name,
        email: em || undefined,
        _model: "crm.prospect",
      });
      return;
    }
    if (
      p.partnerNumericId != null &&
      Number.isFinite(Number(p.partnerNumericId))
    ) {
      setRecipient({
        id: Number(p.partnerNumericId),
        name,
        email: em || undefined,
        _model: "res.partner",
      });
      return;
    }
    if (em && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setRecipient(null);
      setRecipients([{ id: em, label: em }]);
      setRecipientInput("");
    }
  }, [isOpen, pipelineEmailPrefill]);

  useEffect(() => {
    if (proposalPdfClient?.email) {
      setRecipient(proposalPdfClient);
    }
  }, [proposalPdfClient]);

  const isSingleArtwork = selectedArtworks.length === 1;
  const needsPdfModeForConfirm =
    pdfModalAction === "download" ||
    pdfModalAction === "view" ||
    pdfModalAction === "copyPdfLink";

  const isConfirmEnabled =
    typeof includePrice === "boolean" &&
    (!needsPdfModeForConfirm || pdfMode !== null);
  // PDF generation helpers (multi-artwork)

  const loadImageData = async (
    url?: string | null,
  ): Promise<{
    dataUrl: string;
    format: "PNG" | "JPEG";
    width: number;
    height: number;
  } | null> => {
    if (!url || typeof url !== "string") return null;
    const trimmed = String(url).trim();
    if (
      !trimmed ||
      trimmed.toLowerCase() === "false" ||
      trimmed.toLowerCase() === "null"
    )
      return null;
    try {
      const { default: odooClient } = await import("../services/odooClient");
      const absoluteUrl = trimmed.startsWith("http")
        ? trimmed
        : `${(odooClient as any)["baseURL"] || ""}${trimmed}`;
      const backendUrl =
        (odooClient as any)["backendURL"] ||
        process.env.REACT_APP_BACKEND_URL ||
        "";
      const proxiedUrl = backendUrl
        ? `${backendUrl}/proxy-image?url=${encodeURIComponent(absoluteUrl)}`
        : absoluteUrl;

      // Cache
      const cached = imageCacheRef.current.get(proxiedUrl);
      if (cached) return cached;

      const resp = await fetch(proxiedUrl);
      if (!resp.ok) return null;
      const blob = await resp.blob();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result));
        fr.onerror = () => reject(fr.error);
        fr.readAsDataURL(blob);
      });
      const isPng =
        (blob.type && blob.type.toLowerCase().includes("png")) ||
        dataUrl.startsWith("data:image/png");
      const img = new Image();
      img.src = dataUrl;
      await new Promise((r) => {
        img.onload = () => r(0);
      });
      const value = {
        dataUrl,
        format: (isPng ? "PNG" : "JPEG") as "PNG" | "JPEG",
        width: img.width,
        height: img.height,
      };
      imageCacheRef.current.set(proxiedUrl, value);
      return value;
    } catch {
      return null;
    }
  };


  const addArtworkPages = async (
    doc: jsPDF,
    a: any,
    includePrice: boolean = false,
  ) => {

    const normalize = (v: any): string | undefined => {
      if (typeof v !== "string") return undefined;
      const t = v.trim();
      if (!t || t.toLowerCase() === "false" || t.toLowerCase() === "null")
        return undefined;
      return t;
    };

    const cleanText = (t: any) => cleanSanitizedText(t);
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const addLogo = async () => {
      doc.setFont("helvetica", "bold");

      doc.setFontSize(16);

      doc.text("ART GALLERY", 0.75, 0.9);
      doc.setFont("helvetica", "normal");
    };

    const addWrappedText = (
      text: string,
      x: number,
      y: number,
      maxWidth: number,
      fontSize: number,
      lineHeightFactor = 1.2,
    ) => {
      doc.setFontSize(fontSize);
      const lineHeight = (fontSize * lineHeightFactor) / 72;
      const lines = doc.splitTextToSize(text, maxWidth);
      const bottomMargin = 0.77;
      const maxY = pageH - bottomMargin;
      lines.forEach((line: string) => {
        if (y + lineHeight > maxY) {
          doc.addPage("a4", "portrait");
          addLogo();
          y = 0.94 + 0.65;
        }
        doc.text(line, x, y, { align: "left" });
        y += lineHeight;
      });
      return y;
    };

    const addSection = (
      title: string,
      content: string,
      x = 0.77,
      y: number,
      maxWidth = pageW - 1.54,
      fontSize = 11,
      isBoldTitle = true,
    ) => {
      if (isBoldTitle) doc.setFont("helvetica", "bold");
      y = addWrappedText(title, x, y, maxWidth, fontSize);
      doc.setFont("helvetica", "normal");
      if (content) {
        y += ((fontSize * 1.2) / 72) * 0.5;
        const items = content.split("\n").filter((item) => item.trim());
        items.forEach((item) => {
          y = addWrappedText(item, x, y, maxWidth, fontSize);
        });
      }
      return y;
    };

    // Load primary image
    const coverUrl =
      getPrimaryArtworkImageUrl(a) ||
      normalize((a as any)?.main_a6_picture) ||
      normalize((a as any)?.main_a5_picture) ||
      normalize(a?.main_hd_web_picture) ||
      normalize(a.detail_1_url) ||
      normalize(a.other_url) ||
      normalize(a.perspective_url);

    const coverLoaded = await loadImageData(coverUrl);

    await addLogo();

    if (coverLoaded) {
      const boxX = 0.77;
      const boxY = 1.6;
      const boxW = 6.99;
      const boxH = 6.87;
      let w = coverLoaded.width;
      let h = coverLoaded.height;
      const ratio = w / h;
      if (ratio > 1) {
        w = boxW;
        h = boxW / ratio;
      } else {
        h = boxH;
        w = boxH * ratio;
      }
      const imgX = boxX + (boxW - w) / 2;
      const imgY = boxY + (boxH - h) / 2;
      doc.addImage(coverLoaded.dataUrl, coverLoaded.format, imgX, imgY, w, h);
    }

    const artist = Array.isArray(a?.res_artist_id)
      ? a?.res_artist_id[1] || "Unknown Artist"
      : a?.res_artist_id || "Unknown Artist";
    let title = a?.name || "";
    const year = a?.artwork_year || "";
    if (year && title.endsWith(`, ${year}`)) {
      title = title.substring(0, title.length - `, ${year}`.length).trim();
    }
    let detailsY = coverLoaded ? 1.6 + 6.87 + 0.65 : 1.6;
    const leftX = 0.77;
    const maxDetailWidth = 4.73;
    const priceX = 5.5;
    let artistDates = artist;
    if (a?.artist_birth_year) {
      artistDates += ` (${a.artist_birth_year}${
        a?.artist_death_year ? `–${a.artist_death_year}` : ""
      })`;
    }
    doc.setFont("helvetica", "bold");

    const artistKey = String(artistDates).toLowerCase();

    if (!renderedArtistsRef.current.has(artistKey)) {
      renderedArtistsRef.current.add(artistKey);
      doc.setFont("helvetica", "bold");
      detailsY = addWrappedText(
        artistDates,
        leftX,
        detailsY,
        maxDetailWidth,
        13,
      );
    }
    // detailsY = addWrappedText(artistDates, leftX, detailsY, maxDetailWidth, 13);

    if (title) {
      doc.setFont("helvetica", "italic");
      detailsY = addWrappedText(title, leftX, detailsY, maxDetailWidth, 13);
    }
    doc.setFont("helvetica", "normal");
    if (a?.artwork_signed) {
      detailsY = addWrappedText(
        String(a.artwork_signed),
        leftX,
        detailsY,
        maxDetailWidth,
        13,
      );
    }
    if (a?.Medium) {
      detailsY = addWrappedText(
        String(a.Medium),
        leftX,
        detailsY,
        maxDetailWidth,
        13,
      );
    }
    let measurements = "";
    if (a?.SizeH && a?.SizeL && a?.SizeIncH && a?.SizeIncL) {
      measurements = `${a.SizeH} x ${a.SizeL} cm | ${a.SizeIncH} x ${a.SizeIncL} in`;
    } else if (a?.SizeH && a?.SizeL) {
      measurements = `${a.SizeH} x ${a.SizeL} cm`;
    } else if (a?.SizeIncH && a?.SizeIncL) {
      measurements = `${a.SizeIncH} x ${a.SizeIncL} in`;
    }
    if (measurements) {
      detailsY = addWrappedText(
        measurements,
        leftX,
        detailsY,
        maxDetailWidth,
        13,
      );
    }
    if (year) {
      detailsY = addWrappedText(
        `Executed ${year}`,
        leftX,
        detailsY,
        maxDetailWidth,
        13,
      );
    }


    if (includePrice) {
      const artworkForPrice: any = {
        ...a,
        prices: {
          CHF: (a as any).PriceCHFEnt,
          EUR: (a as any).PriceEUREnt,
          GBP: (a as any).PriceGBPEnt,
          HKD: (a as any).PriceHKDEnt,
          KRW: (a as any).PriceKRWEnt,
          AED: (a as any).PriceAEDEnt,
          USD: (a as any).PriceUSDEnt,
          SGD: (a as any).PriceSGDEnt,
        },
        PriceRef: (a as any).PriceRef,
        CurrencyRef: (a as any).CurrencyRef,
      };
      const { price, currency: displayCurrency } = getArtworkPrice(
        artworkForPrice,
        selectedCurrency,
      );
      const priceText =
        price > 0
          ? formatPriceForPDF(price, displayCurrency as CurrencyCode, false)
          : "Price on Request";
      doc.text(priceText, priceX, detailsY - (13 * 1.2) / 72, {
        align: "left",
      });
    } else {
      doc.text("Price on Request", priceX, detailsY - (13 * 1.2) / 72, {
        align: "left",
      });
    }

    const descriptionText = normalizeWeirdSpacing(a?.artwork_description || "");
    const provenanceText = normalizeWeirdSpacing(
      (a as any)?.ProvenanceWordingCrm,
    );

    const exhibitionsText = normalizeWeirdSpacing(a?.artwork_exhibited);
    const literatureText = normalizeWeirdSpacing(a?.LiteratureEn || "").replace(
      /\r\n?/g,
      "\n",
    );
    const hasPage3Content = Boolean(
      descriptionText || provenanceText || exhibitionsText || literatureText,
    );
    if (hasPage3Content) {
      doc.addPage("a4", "portrait");
      await addLogo();
      let y = 0.94 + 0.65;
      doc.setFontSize(11);
      const page3Title = year ? `${title} (${year})` : title;
      doc.setFont("helvetica", "italic");
      y = addWrappedText(page3Title, 0.77, y, pageW - 1.54, 11);
      doc.setFont("helvetica", "normal");
      if (descriptionText) {
        const paras = descriptionText
          .split(/\n\n+/)
          .filter((p: string) => p.trim());
        paras.forEach((para: string, index: number) => {
          if (index > 0) y += ((11 * 1.2) / 72) * 1;
          y = addWrappedText(para, 0.77, y, pageW - 1.54, 11);
        });
        y += ((11 * 1.2) / 72) * 5;
      }
      const parseProvenanceSections = (text: string) => {
        const sections: { label: string; value: string }[] = [];
        if (!text) return sections;
        const normalizedText = text.replace(/\r\n?/g, "\n");
        const regex = /(CERTIFICATE|EXHIBITED|PROVENANCE)\s*:\s*/gi;
        let lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = regex.exec(normalizedText)) !== null) {
          if (lastIndex < match.index) {
            const prevContent = normalizedText
              .substring(lastIndex, match.index)
              .trim();
            if (sections.length > 0 && prevContent) {
              sections[sections.length - 1].value += "\n" + prevContent;
            }
          }
          const label =
            match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
          lastIndex = regex.lastIndex;
          sections.push({ label, value: "" });
        }
        if (lastIndex < normalizedText.length) {
          const remaining = normalizedText.substring(lastIndex).trim();
          if (sections.length > 0) {
            sections[sections.length - 1].value += "\n" + remaining;
          }
        }
        return sections;
      };
      const sections = parseProvenanceSections(provenanceText);
      sections.forEach((sec) => {
        if (sec.value.trim()) {
          y = addSection(sec.label, sec.value, 0.77, y);
          y += ((11 * 1.2) / 72) * 3;
        }
      });
      if (exhibitionsText) {
        y = addSection("Exhibitions", exhibitionsText, 0.77, y);
        y += ((11 * 1.2) / 72) * 3;
      }
      if (literatureText) {
        y = addSection("Literature", literatureText, 0.77, y);
      }
    }

    if (coverLoaded) {
      doc.addPage("a4", "portrait");
      await addLogo();
      const maxW = 8.3;
      const maxH = 9.8;
      const ratio = coverLoaded.width / coverLoaded.height;
      let w: number, h: number, y: number;
      if (ratio > 1) {
        w = maxW;
        h = maxW / ratio;
        y = pageH - maxH + (maxH - h) / 2;
      } else {
        h = maxH;
        w = maxH * ratio;
        y = pageH - h;
      }
      const x = (pageW - w) / 2;
      doc.addImage(coverLoaded.dataUrl, coverLoaded.format, x, y, w, h);
    }

    const bioText = cleanText(a?.artist_bio || "");
    const collectionsText = cleanText(a?.selected_collections || "");
    const hasPage6Content = Boolean(bioText || collectionsText);
    if (hasPage6Content) {
      doc.addPage("a4", "portrait");
      await addLogo();
      let y = 0.94 + 0.65;
      const fontSize = 11;
      doc.setFontSize(fontSize);
      doc.setFont("helvetica", "bold");
      const artistName = artist;
      y = addWrappedText(artistName, 0.77, y, pageW - 1.54, fontSize);
      if (bioText) {
        doc.setFont("helvetica", "normal");
        const normalizedBioText = bioText.replace(/\r\n?/g, "\n");
        y += ((fontSize * 1.2) / 72) * 2;
        const paras = normalizedBioText
          .split(/\n\n+/)
          .filter((p: string) => p.trim());
        paras.forEach((para: string, index: number) => {
          if (index > 0) y += ((fontSize * 1.2) / 72) * 2;
          y = addWrappedText(para, 0.77, y, pageW - 1.54, fontSize);
        });
        y += ((fontSize * 1.2) / 72) * 2;
      }
      if (collectionsText) {
        doc.setFont("helvetica", "normal");
        y = addSection(
          "Selected Public Collections",
          collectionsText,
          0.77,
          y,
          pageW - 1.54,
          fontSize,
        );
      }
    }
  };

  const pdfImageCacheRef = useRef<
    Map<
      string,
      { dataUrl: string; format: "PNG" | "JPEG"; width: number; height: number }
    >
  >(new Map());

  /** Mock artwork with all fields filled for test PDF (Page 1–6). Uses same-origin image URLs so they load. */
  const getMockArtworkForTestPDF = (publicBase: string): any => ({
    id: 99999,
    name: "Sample Artwork, 2024",
    create_date: "2024-01-15",
    artwork_year: "2024",
    res_artist_id: [1, "Sample Artist"],
    artist_birth_year: "1970",
    artist_death_year: null,
    ProvenanceWordingCrm:
      "Private collection, Europe. Acquired from the artist's studio in 2023. Previously in the collection of [Previous Owner], Paris.",
    artwork_exhibited:
      "Galerie Example, Paris, 2023. Museum of Modern Art, New York, 2022. Venice Biennale, 2021.",
    LiteratureEn:
      "Sample Artist: A Retrospective, Exhibition Catalogue, Paris 2023. Art Monthly, Vol. 45, 2022.",
    CertificateWording:
      "This work is accompanied by a certificate of authenticity signed by the artist and the estate.",
    artist_bio:
      "Sample Artist (b. 1970) is a contemporary artist known for large-scale works. Their work has been exhibited internationally and is held in major public and private collections.",
    selected_collections:
      "Museum of Modern Art, New York; Centre Pompidou, Paris; Tate Modern, London.",
    main_super_picture_hd: `${publicBase}/background.png`,
    main_web_picture: `${publicBase}/background.png`,
    frame_picture: `${publicBase}/background.png`,
    detail_1_url: `${publicBase}/background.png`,
    detail_2_url: `${publicBase}/person.png`,
    other_url: `${publicBase}/background.png`,
    Category: "Painting",
    SizeIncH: 120,
    SizeIncL: 80,
    artwork_signed: "Signed and dated lower right",
    medium: "Oil on canvas",
    dimensions: "120 × 80 cm",
    list_price: 50000,
    artwork_price_combined: 50000,
  });

  // Multi-artwork proposal PDF generator (merged design)
  const handleDownloadProposalPDF = async (
    includePrice: boolean = false,
    mode: "single" | "multiple",
    action: "download" | "view" | "attach" = "download", // Support 'attach' for email
    useMockData: boolean = false,
    onSinglePdfReadyOverride?: (blob: Blob, artwork: any) => void | Promise<void>,
    options?: { suppressDownloadLoading?: boolean },
  ) => {
    const suppressDownloadLoading = Boolean(options?.suppressDownloadLoading);
    const singlePdfReadyHandler = onSinglePdfReadyOverride || onSinglePdfReady;
    // Keep artist-bio tracking scoped to a single generation run.
    // This avoids cross-talk when two PDF generations happen close together.
    const renderedArtists = new Set<string>();
    const formatSkippedArtworksMessage = (items: any[]) => {
      if (!items.length) return "";

      const lines = items.map((artwork: any, index: number) => {
        const label = String(
          artwork?.name || artwork?.title || artwork?.IdName || artwork?.id || "",
        ).trim();
        return `${index + 1}. ${label || "Untitled artwork"}: missing main image`;
      });

      return [
        `PDF generated for ${count - items.length} of ${count} artwork${count === 1 ? "" : "s"}.`,
        "",
        "These artwork PDFs were skipped because no main image was available:",
        ...lines,
      ].join("\n");
    };
    if (!useMockData && selectedArtworks.length === 0) return;

    const count = useMockData ? 1 : selectedArtworks.length;
    if (!suppressDownloadLoading) {
      setDownloadingPDF(true);
      setDownloadDone(0);
      setDownloadTotal(count);
    }
    // setShowPDFOptionsModal(false);

    try {
      const { default: odooClient } = await import("../services/odooClient");

      // 1) Get artwork data: either fetch from API or use mock for test PDF
      let artworksFull: any[];
      if (useMockData) {
        const publicBase = window.location.origin;
        artworksFull = [getMockArtworkForTestPDF(publicBase)];
      } else {
        const fetchTasks = selectedArtworks.map((sa) => async () => {
          try {
            return await odooClient.getArtworkById(parseInt(sa.id));
          } catch {
            return null;
          }
        });
        artworksFull = await withConcurrency(fetchTasks, 5); // returns array in same order
      }

      // loadImageData (same robust loader you provided)
      const loadImageData = async (
        url?: string | null,
      ): Promise<{
        dataUrl: string;
        format: "PNG" | "JPEG";
        width: number;
        height: number;
      } | null> => {
        if (!url) return null;
        const trimmed = String(url).trim();
        if (!trimmed || ["false", "null"].includes(trimmed.toLowerCase()))
          return null;
        try {
          let absoluteUrl = trimmed.startsWith("http")
            ? trimmed
            : `${(odooClient as any)["baseURL"] || ""}${trimmed}`;
          const fetchUrl = absoluteUrl;
          const cached = pdfImageCacheRef.current.get(fetchUrl);
          if (cached) return cached;

          let resp: Response;
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);
          const backendUrl =
            (odooClient as any)["backendURL"] ||
            process.env.REACT_APP_BACKEND_URL ||
            "";

          if (fetchUrl.startsWith(window.location.origin)) {
            resp = await fetch(fetchUrl, { signal: controller.signal });
          } else {
            if (!backendUrl) throw new Error("Backend URL not configured");
            const proxiedUrl = `${backendUrl}/proxy-image?url=${encodeURIComponent(
              absoluteUrl,
            )}`;
            resp = await fetch(proxiedUrl, { signal: controller.signal });
          }
          clearTimeout(timeoutId);
          if (!resp.ok) {
            console.warn(`Image fetch failed for ${fetchUrl}: ${resp.status}`);
            return null;
          }
          const blob = await resp.blob();
          const dataUrl = await new Promise<string>((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(String(fr.result));
            fr.onerror = () => reject(fr.error);
            fr.readAsDataURL(blob);
          });
          const isPng =
            (blob.type && blob.type.toLowerCase().includes("png")) ||
            dataUrl.startsWith("data:image/png");
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.src = dataUrl;
          await new Promise((r, rej) => {
            const timeoutImg = setTimeout(
              () => rej(new Error("Image load timeout")),
              5000,
            );
            img.onload = () => {
              clearTimeout(timeoutImg);
              r(0);
            };
            img.onerror = () => {
              clearTimeout(timeoutImg);
              rej(new Error("Image load error"));
            };
          });
          await img.decode?.();
          const value = {
            dataUrl,
            format: (isPng ? "PNG" : "JPEG") as "PNG" | "JPEG",
            width: img.width,
            height: img.height,
          };
          pdfImageCacheRef.current.set(fetchUrl, value);
          return value;
        } catch {
          return null;
        }
      };

      // 2) Prefetch key images (cover + one info image) with limited concurrency
      const imageUrls: string[] = [];
      const normalize = (v: any): string | undefined => {
        if (typeof v !== "string") return undefined;
        const t = v.trim();
        if (!t || t.toLowerCase() === "false" || t.toLowerCase() === "null")
          return undefined;
        return t;
      };

      const getCoverUrl = (a: any): string | undefined => {
        if (!a) return undefined;
        // const a5Image =
        //   normalize((a as any).main_a6_picture) ||
        //   normalize((a as any).main_a5_picture);
        const mainImage = getPrimaryArtworkImageUrl(a);
           return  mainImage;
        // return a5Image || mainImage;
      };

      const validArtworks = artworksFull.filter((a) => !!getCoverUrl(a));
      const skippedNoCover = artworksFull.filter((a) => a && !getCoverUrl(a));

      if (validArtworks.length === 0) {
        alert(
           "We can't generate the PDF. We don't have a main image (Super HD, HD, or Web). Please contact support.",
        );
        return;
      }

      artworksFull = validArtworks;
      setDownloadTotal(artworksFull.length);
      if (skippedNoCover.length > 0) {
        console.warn(
         "Skipping artworks without main image (Super HD/HD/Web):",
          skippedNoCover.map((a: any) => a?.name || a?.id),
        );
      }

      const syncDownloadPdfsToCrmIfNeeded = async (
        pdfFiles: { blob: Blob; fileName: string }[],
      ) => {
        if (action !== "download" || onSinglePdfReadyOverride) return;
        if (!proposalPdfClient?.id || pdfFiles.length === 0) return;
        await ensureProposalCrmPipelineForClient(
          proposalPdfClient,
          artworksFull,
          pdfFiles,
          "download",
        );
        // Proposal counter: user downloaded for a known client → bump the
        // (seller, client, artwork) rows so the Artworks list `$` column
        // reflects this action. Fire-and-forget; odooClient already swallows
        // errors so this never blocks the download flow.
        await odooClient.recordArtworkProposalAction(
          selectedArtworks.map((sa: any) => sa?.id),
          proposalPdfClient,
        );
      };

      artworksFull.forEach((a) => {
        if (!a) return;
        const cover = getCoverUrl(a);
        if (cover) imageUrls.push(cover);
        // const cover =
        //   normalize((a as any).main_a6_picture) ||
        //   normalize((a as any).main_a5_picture) ||
        //   normalize(a.main_super_picture_hd) ||
        //   normalize(a.main_web_picture) ||
        //   normalize(a.main_picture_hd) ||
        //   normalize(a.main_hd_web_picture);
        // if (cover) imageUrls.push(cover);
        const info =
          normalize(a.other_url) ||
          normalize(a.frame_picture) ||
          normalize(a.detail_1_url) ||
          // normalize(a.perspective_url) ||
          normalize(a.signatures_pictures) ;
          // normalize(a.back_pictures);
        if (info) imageUrls.push(info);
      });
      const imageTasks = imageUrls.map((u) => () => loadImageData(u));
      await withConcurrency(imageTasks, 6);

      // 3) Render sequentially into a single PDF
      const doc = new jsPDF({
        unit: "in",
        format: "a4",
        orientation: "portrait",
      });

      // Do not register placeholder Adobe Caslon data. It breaks unicode metrics in jsPDF.

      // Register Acumin Pro fonts
      // Register Acumin Pro fonts
      try {
        await registerAcuminProFonts(doc);
      } catch (e) {
        console.warn("Acumin Pro fonts not available, using fallback fonts");
      }

      // Prefetch background/person once
      const publicBase = window.location.origin;
      const silhouetteUrl = `${publicBase}/person.png`;
      const backgroundUrl = `${publicBase}/background.png`;
      const [silhouetteLoaded, backgroundLoaded] =
        await Promise.all([
          loadImageData(silhouetteUrl),
          loadImageData(backgroundUrl),
        ]);

      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      // --- PDF layout config: sample PDFs mark every required spacing box as 10mm. ---
      const MM = 1 / 25.4;
      const TEN_MM = 10 * MM;
      const PDF_LAYOUT = {
        marginInch: TEN_MM,
        sectionTitleY: 1.36, // "Artwork information" baseline (same on all section pages)
        spaceAfterTitle: TEN_MM,
        imageTopY: TEN_MM * 5, // 10mm top margin + four 10mm guide boxes
        imageHeightInch: 7.1, // image block height
        spaceAfterImage: TEN_MM,
        spaceAfterContinuedTitle: TEN_MM,
        artworkDetailTitleY: 1.36, // same as sectionTitleY for equal top space
        artworkDetailSpaceAfterTitle: TEN_MM,
        artistBioTitleY: 1.36, // same as sectionTitleY
        artistBioSpaceAfterTitle: TEN_MM,
        priceRightMarginPx: 115,
        captionPriceBottomMarginInch: TEN_MM,
      };
      const SHOW_PDF_ALIGNMENT_GUIDES = false; // set true to draw red/yellow guides and overlay on reference

      const M = PDF_LAYOUT.marginInch;
      const leftX = M;
      const maxW = pageW - 2 * M;
      const rightX = pageW - M;
      // Price column start (right-side column, left-aligned text).
      // Tuned to match client PDF guide overlay.
      const priceColumnX = leftX + maxW * 0.67 + 27 / 96;
      // Keep a fixed blank gutter between left caption text and right price column.
      const captionPriceGap = TEN_MM;
      const captionTextMaxW = Math.max(
        1,
        priceColumnX - captionPriceGap - leftX,
      );
      const captionBlockShiftY = 0;

      const bottomM = M;

      const drawAlignmentGuides = (
        doc: jsPDF,
        kind:
          | "margins"
          | "artwork-info"
          | "artwork-continued"
          | "artwork-detail"
          | "artist-bio",
      ) => {
        if (!SHOW_PDF_ALIGNMENT_GUIDES) return;
        const pw = doc.internal.pageSize.getWidth();
        const ph = doc.internal.pageSize.getHeight();
        doc.setLineWidth(0.005);

        // Red = alignment (margins and key baselines)
        doc.setDrawColor(255, 0, 0);
        doc.line(leftX, 0, leftX, ph);
        doc.line(rightX, 0, rightX, ph);
        doc.line(0, M, pw, M);
        doc.line(0, ph - bottomM, pw, ph - bottomM);

        if (kind === "artwork-info") {
          doc.line(0, PDF_LAYOUT.sectionTitleY, pw, PDF_LAYOUT.sectionTitleY);
          doc.line(0, PDF_LAYOUT.imageTopY, pw, PDF_LAYOUT.imageTopY);
          doc.line(
            0,
            PDF_LAYOUT.imageTopY + PDF_LAYOUT.imageHeightInch,
            pw,
            PDF_LAYOUT.imageTopY + PDF_LAYOUT.imageHeightInch,
          );
          doc.line(
            0,
            PDF_LAYOUT.imageTopY +
              PDF_LAYOUT.imageHeightInch +
              PDF_LAYOUT.spaceAfterImage,
            pw,
            PDF_LAYOUT.imageTopY +
              PDF_LAYOUT.imageHeightInch +
              PDF_LAYOUT.spaceAfterImage,
          );
        }
        if (kind === "artwork-continued") {
          doc.line(0, PDF_LAYOUT.sectionTitleY, pw, PDF_LAYOUT.sectionTitleY);
        }
        if (kind === "artwork-detail" || kind === "artist-bio") {
          doc.line(
            0,
            PDF_LAYOUT.artworkDetailTitleY,
            pw,
            PDF_LAYOUT.artworkDetailTitleY,
          );
          const detailImgTop = PDF_LAYOUT.imageTopY;
          doc.line(0, detailImgTop, pw, detailImgTop);
        }

        // Yellow = spacing (gaps)
        doc.setDrawColor(255, 255, 0);
        doc.line(
          leftX,
          PDF_LAYOUT.sectionTitleY + (18 * 1.2) / 72,
          rightX,
          PDF_LAYOUT.sectionTitleY + (18 * 1.2) / 72,
        );
        doc.line(
          leftX,
          PDF_LAYOUT.imageTopY - PDF_LAYOUT.spaceAfterTitle,
          rightX,
          PDF_LAYOUT.imageTopY - PDF_LAYOUT.spaceAfterTitle,
        );
        doc.line(
          leftX,
          PDF_LAYOUT.imageTopY +
            PDF_LAYOUT.imageHeightInch +
            PDF_LAYOUT.spaceAfterImage * 0.5,
          rightX,
          PDF_LAYOUT.imageTopY +
            PDF_LAYOUT.imageHeightInch +
            PDF_LAYOUT.spaceAfterImage * 0.5,
        );
      };

      // Text wordmark header (the old Opera Gallery logo image is no longer used).
      const addLogo = (doc: jsPDF) => {
        doc.setFont("times", "normal");
        doc.setFontSize(19);
        (doc as any).setCharSpace && (doc as any).setCharSpace(0.01);
        doc.text("ART GALLERY", M, 0.6);
        (doc as any).setCharSpace && (doc as any).setCharSpace(0);
      };


      doc.deletePage(1);


      // Add wrapped text helper (uses 10mm bottom margin)
      const addWrappedText = (
        doc: jsPDF,
        text: string,
        x: number,
        y: number,
        maxWidth: number,
        fontSize: number,
        lineHeightFactor = 1.2,
        maxYOverride?: number,
      ) => {
        doc.setFontSize(fontSize);
        const lineHeight = (fontSize * lineHeightFactor) / 72;
        // splitTextToSize requires a font with Unicode metrics; custom fonts (Acumin/Adobe Caslon) can throw. Use helvetica for splitting then restore.
        const internal = (doc as any).internal;
        const currentFont = internal?.getFont?.() || {};
        const fontName = currentFont.fontName || "helvetica";
        const fontStyle = currentFont.fontStyle || "normal";
        doc.setFont("helvetica", "normal");
        const lines = doc.splitTextToSize(text, maxWidth);
        try {
          doc.setFont(fontName, fontStyle);
        } catch {
          doc.setFont("helvetica", "normal");
        }
        const maxY = maxYOverride ?? pageH - bottomM;
        for (const line of lines) {
          if (y + lineHeight > maxY) {
            doc.addPage();
            addLogo(doc);
            drawAlignmentGuides(doc, "margins");
            y = M + 0.6;
          }
          // We already wrapped the line above; avoid jsPDF re-measurement to keep alignment stable.
          doc.text(line, x, y, { align: "left" });
          y += lineHeight;
        }
        return y;
      };

      const cleanText = (t: any) => cleanSanitizedText(t);

      const getArtistMeta = (a: any) => {
        const artist = Array.isArray(a?.res_artist_id)
          ? a?.res_artist_id[1] || "Unknown Artist"
          : a?.res_artist_id || "Unknown Artist";
        const nameNorm = String(artist)
          .trim()
          .toLowerCase()
          .replace(/[,.]/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        const nameKey = nameNorm.split(/\s+/).filter(Boolean).sort().join(" ");
        const yearNorm =
          /^\d{4}/.exec(String(a?.artist_birth_year ?? ""))?.[0] ?? "";
        const artistKey = `${nameKey}_${yearNorm}`;
        return { artist, artistKey };
      };

      const addArtistBioPage = (doc: jsPDF, a: any) => {
        const { artist, artistKey } = getArtistMeta(a);
        const bioText = cleanText(a?.artist_bio || "");
        const collectionsText = cleanText(a?.selected_collections || "");
        if (!bioText && !collectionsText) return;
        if (renderedArtists.has(artistKey)) return;

        renderedArtists.add(artistKey);
        doc.addPage();
        addLogo(doc);
        drawAlignmentGuides(doc, "artist-bio");
        let y = PDF_LAYOUT.artistBioTitleY;

        try {
          doc.setFont("AcuminPro", "normal");
        } catch {
          doc.setFont("times", "roman");
        }
        doc.setFontSize(18);
        doc.text("Artist biography", leftX, y);
        y = PDF_LAYOUT.imageTopY + 14 / 72;

        try {
          doc.setFont("AcuminPro", "bold");
        } catch {
          doc.setFont("helvetica", "bold");
        }
        doc.setFontSize(14);
        const artistDatesBio =
          artist +
          (a.artist_birth_year
            ? ` (${a.artist_birth_year}${
                a.artist_death_year ? `-${a.artist_death_year}` : ""
              })`
            : "");
        y = addWrappedText(doc, artistDatesBio, leftX, y, maxW, 14);

        if (bioText) {
          try {
            doc.setFont("AcuminPro", "normal");
          } catch {
            doc.setFont("helvetica", "normal");
          }
          doc.setFontSize(14);
          const normalizedBioText = bioText.replace(/\r\n?/g, "\n");
          y += ((14 * 1.2) / 72) * 0.5;
          const paras = normalizedBioText
            .split(/\n\n+/)
            .filter((p: string) => p.trim());
          paras.forEach((para: string, idx: number) => {
            if (idx > 0) y += ((14 * 1.2) / 72) * 0.5;
            y = addWrappedText(doc, para, leftX, y, maxW, 14);
          });
        }

        if (collectionsText) {
          try {
            doc.setFont("AcuminPro", "bold");
          } catch {
            doc.setFont("helvetica", "bold");
          }
          doc.setFontSize(14);
          y += ((14 * 1.2) / 72) * 0.5;
          y = addWrappedText(doc, "Selected Public Collections", leftX, y, maxW, 14);
          try {
            doc.setFont("AcuminPro", "normal");
          } catch {
            doc.setFont("helvetica", "normal");
          }
          doc.setFontSize(14);
          y = addWrappedText(doc, collectionsText, leftX, y, maxW, 14);
        }
      };

      // Per-artwork renderer (keeps your visual flow)
      const addArtworkPages = async (
        doc: jsPDF,
        a: any,
        renderArtistBio: boolean = false,
      ) => {
        if (!a) return;

        const coverUrl =
          getPrimaryArtworkImageUrl(a) ||
          normalize((a as any).main_hd_web_picture) ||
          normalize((a as any).main_a6_picture) ||
          normalize((a as any).main_a5_picture);

        if (!coverUrl) {
          return false;
        }

        // const detailUrl =
        //   normalize(a.other_url) ||
        //   normalize(a.frame_picture) ||
        //   normalize(a.detail_1_url) ||
        //   // normalize(a.perspective_url) ||
        //   perspectiveHD ||
        //   normalize(a.signatures_pictures) ||
        //   normalize(a.back_pictures);

        // const det1Url = normalize(a.detail_1_url);
        // const det2Url = normalize(a.detail_2_url);

        const coverLoadedLocal = (await loadImageData(coverUrl)) || null;
        // const detailLoadedLocal =
        //   (detailUrl ? await loadImageData(detailUrl) : null) ||
        //   coverLoadedLocal;

        // const det1Loaded = det1Url ? await loadImageData(det1Url) : null;
        // const det2Loaded = det2Url ? await loadImageData(det2Url) : null;

        // String fields
        let titleRaw = (a.name || "").trim();
        let dateToDisplay = a.artwork_year || "";

        // 1. Prepare date string
        if (
          typeof dateToDisplay === "string" &&
          dateToDisplay.match(/^\d{4}/)
        ) {
          dateToDisplay = dateToDisplay.substring(0, 4);
        } else if (dateToDisplay) {
          dateToDisplay = String(dateToDisplay);
        }

        // 2. Fallback: Parse from title if missing
        if (!dateToDisplay) {
          const match = titleRaw.match(/, ?(\d{4})$/);
          if (match) {
            dateToDisplay = match[1];
          }
        }

        let title = titleRaw;
        // 3. Clean title if it ends with the date we are displaying
        if (dateToDisplay && title.endsWith(`, ${dateToDisplay}`)) {
          title = title
            .substring(0, title.length - `, ${dateToDisplay}`.length)
            .trim();
        }

        const artist = Array.isArray(a.res_artist_id)
          ? a.res_artist_id[1] || "Unknown Artist"
          : a.res_artist_id || "Unknown Artist";
        const templateLayout = normalizeProposalPdfTemplateLayout(
          (a as any).CRMTypeLayout,
        );
        const renderContinuedInfo = templateLayout !== "contemporary";
        const renderMasterMediaPages = templateLayout === "master";
        const renderedMediaUrls = new Set<string>();

        const addImageSectionPage = async (
          title: string,
          urls: Array<string | undefined>,
        ) => {
          for (const rawUrl of urls) {
            const imageUrl = normalize(rawUrl);
            if (!imageUrl || renderedMediaUrls.has(imageUrl)) continue;

            const imgLoaded = await loadImageData(imageUrl);
            if (!imgLoaded) continue;

            renderedMediaUrls.add(imageUrl);
            doc.addPage();
            addLogo(doc);
            drawAlignmentGuides(doc, "artwork-detail");

            try {
              doc.setFont("AcuminPro", "normal");
            } catch {
              doc.setFont("times", "roman");
            }
            doc.setFontSize(18);
            doc.text(title, leftX, PDF_LAYOUT.artworkDetailTitleY);

            const boxX = leftX;
            const boxY = PDF_LAYOUT.imageTopY;
            const boxW = maxW;
            const boxH = pageH - boxY - bottomM;
            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, Math.round(boxW * 144));
            canvas.height = Math.max(1, Math.round(boxH * 144));
            const ctx = canvas.getContext("2d");
            if (!ctx) return true;

            const img = await new Promise<HTMLImageElement>((resolve, reject) => {
              const el = new Image();
              el.onload = () => resolve(el);
              el.onerror = () => reject(new Error("Image load error"));
              el.src = imgLoaded.dataUrl;
            });
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            const scale = canvas.width / img.width;
            const drawW = img.width * scale;
            const drawH = img.height * scale;
            const drawX = 0;
            const drawY = 0;
            ctx.drawImage(img, drawX, drawY, drawW, drawH);
            doc.addImage(
              canvas.toDataURL("image/jpeg", 0.92),
              "JPEG",
              boxX,
              boxY,
              boxW,
              boxH,
              undefined,
              "FAST",
            );
            return true;
          }

          return false;
        };

        // --- CLIENT SPEC: Full pattern (as per attached PDF) ---
        // Page 1: Artwork information → Photo below → Caption at the bottom
        // Page 2 (and possibly spanning 2 pages): Artwork information continued — Signature, Provenance, Exhibited, Literature, Certificate
        // Page 3: Artwork at scale
        // Page 4: Artist biography (once per artist if multiple works by same artist)
        // Pages 5 & 6: Artwork detail pictures
        // ---

        // --- PAGE 1: artwork image + caption (no section heading) ---
        doc.addPage();
        addLogo(doc);
        drawAlignmentGuides(doc, "margins");
        drawAlignmentGuides(doc, "artwork-info");

        // Caption at the bottom (as per attached PDF): Artist → Title → Date → Medium → Edition → Dimensions | Price on Request
        // If the block is short, we want it to sit closer to the bottom, not directly under the image.
        const captionLineHeight14 = (14 * 1.2) / 72;
        // jsPDF positions text by baseline. The sample's 10mm yellow gap is
        // measured to the visual top of "KAWS", so reserve the first line's
        // ascent when placing the image above the caption.
        const captionFirstLineAscent = 14 / 72;

        // Build caption pieces
        let artistDates = artist;
        if (a.artist_birth_year) {
          artistDates += ` (${a.artist_birth_year}${
            a.artist_death_year ? ` - ${a.artist_death_year}` : ""
          })`;
        }
        const mediumText = (a.Medium || a.medium || "").trim();
        const editionTextClean = getArtworkEditionText(a);

        // Dimensions (e.g. "64.8 x 49.5 cm | 25.5 x 19.5 in") — Acumin Pro Regular 14pt
        let measurements = "";
        if (a.SizeH && a.SizeL && a.SizeIncH && a.SizeIncL) {
          measurements = `${a.SizeH} x ${a.SizeL} cm | ${a.SizeIncH} x ${a.SizeIncL} in`;
        } else if (a.SizeH && a.SizeL) {
          measurements = `${a.SizeH} x ${a.SizeL} cm`;
        } else if (a.SizeIncH && a.SizeIncL) {
          measurements = `${a.SizeIncH} x ${a.SizeIncL} in`;
        } else if (a.dimensions) {
          measurements = String(a.dimensions).trim();
        }

        // --- measure caption block height ---
        // Use a built-in font for splitTextToSize; custom fonts (Acumin/Adobe Caslon) may lack Unicode metrics and throw.
        doc.setFont("helvetica", "normal");
        doc.setFontSize(14);
        let captionHeight = 0;

        // Artist
        {
          const lines = doc.splitTextToSize(artistDates, captionTextMaxW);
          captionHeight += lines.length * captionLineHeight14;
        }

        // Title
        if (title) {
          const lines = doc.splitTextToSize(title, captionTextMaxW);
          captionHeight += lines.length * captionLineHeight14;
        }

        // Date
        if (dateToDisplay) {
          const lines = doc.splitTextToSize(dateToDisplay, captionTextMaxW);
          captionHeight += lines.length * captionLineHeight14;
        }

        // Medium
        if (mediumText) {
          const lines = doc.splitTextToSize(mediumText, captionTextMaxW);
          captionHeight += lines.length * captionLineHeight14;
        }

        // Edition
        if (editionTextClean) {
          const lines = doc.splitTextToSize(editionTextClean, captionTextMaxW);
          captionHeight += lines.length * captionLineHeight14;
        }

        // Measurements
        if (measurements) {
          const lines = doc.splitTextToSize(measurements, captionTextMaxW);
          captionHeight += lines.length * captionLineHeight14;
        }

        // Anchor from BOTTOM: last line baseline is exactly one 10mm box above the page bottom.
        // In jsPDF, doc.text(y) is the baseline; addWrappedText returns the Y after the last line (next line position), so last line baseline = returned Y - lineHeight.
        const captionBottomTarget = pageH - bottomM + captionBlockShiftY;
        const minCaptionTop = PDF_LAYOUT.imageTopY + PDF_LAYOUT.spaceAfterImage;
        // Let caption draw down to this Y so we don't add a new page (keeps "100 x 81 cm" and "Price on Request" on page 1).
        const captionMaxY = captionBottomTarget + captionLineHeight14;
        let detailsY =
          captionBottomTarget - captionHeight + captionLineHeight14;
        if (detailsY < minCaptionTop) {
          // Caption too tall: start below image so we stay on page 1; price will align with last line (below captionBottomTarget).
          detailsY = minCaptionTop;
        }

        // Photo above caption: keep the rendered box on the 10mm grid.
        // Landscape works use contain/top-align so the full image stays visible;
        // any extra space is allowed at the bottom.
        if (coverLoadedLocal) {
          const boxX = leftX;
          const boxY = PDF_LAYOUT.imageTopY;
          const boxW = maxW;
          const boxH = Math.max(
            0.5,
            detailsY - captionFirstLineAscent - PDF_LAYOUT.spaceAfterImage - boxY,
          );
          const canvas = document.createElement("canvas");
          canvas.width = Math.max(1, Math.round(boxW * 144));
          canvas.height = Math.max(1, Math.round(boxH * 144));
          const ctx = canvas.getContext("2d");
          if (ctx) {
            const img = await new Promise<HTMLImageElement>((resolve, reject) => {
              const el = new Image();
              el.onload = () => resolve(el);
              el.onerror = () => reject(new Error("Image load error"));
              el.src = coverLoadedLocal.dataUrl;
            });
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            const scale = Math.min(
              canvas.width / img.width,
              canvas.height / img.height,
            );
            const drawW = img.width * scale;
            const drawH = img.height * scale;
            const drawX = (canvas.width - drawW) / 2;
            const drawY = 0;
            ctx.drawImage(img, drawX, drawY, drawW, drawH);
            doc.addImage(
              canvas.toDataURL("image/jpeg", 0.92),
              "JPEG",
              boxX,
              boxY,
              boxW,
              boxH,
              undefined,
              "FAST",
            );
          }
        }

        // Artist — Acumin Pro Bold 14pt (e.g. "Bernard BUFFET (1928 - 1999)")
        try {
          doc.setFont("AcuminPro", "bold");
        } catch {
          doc.setFont("helvetica", "bold");
        }
        doc.setFontSize(14);
        detailsY = addWrappedText(
          doc,
          artistDates,
          leftX,
          detailsY,
          captionTextMaxW,
          14,
          1.2,
          captionMaxY,
        );

        // Title — Acumin Pro Italic 14pt (line 1, line 2)
        if (title) {
          try {
            doc.setFont("AcuminPro", "italic");
          } catch {
            doc.setFont("helvetica", "italic");
          }
          doc.setFontSize(14);
          detailsY = addWrappedText(
            doc,
            title,
            leftX,
            detailsY,
            captionTextMaxW,
            14,
            1.2,
            captionMaxY,
          );
        }

        // Date — Acumin Pro Regular 14pt
        if (dateToDisplay) {
          try {
            doc.setFont("AcuminPro", "normal");
          } catch {
            doc.setFont("helvetica", "normal");
          }
          doc.setFontSize(14);
          detailsY = addWrappedText(
            doc,
            dateToDisplay,
            leftX,
            detailsY,
            captionTextMaxW,
            14,
            1.2,
            captionMaxY,
          );
        }

        // Medium line 1, line 2 — Acumin Pro Regular 14pt
        try {
          doc.setFont("AcuminPro", "normal");
        } catch {
          doc.setFont("helvetica", "normal");
        }
        doc.setFontSize(14);
        if (mediumText) {
          detailsY = addWrappedText(
            doc,
            mediumText,
            leftX,
            detailsY,
            captionTextMaxW,
            14,
            1.2,
            captionMaxY,
          );
        }

        // Edition Number — Acumin Pro Regular 14pt (if present)
        if (editionTextClean) {
          detailsY = addWrappedText(
            doc,
            editionTextClean,
            leftX,
            detailsY,
            captionTextMaxW,
            14,
            1.2,
            captionMaxY,
          );
        }

        // Dimensions
        if (measurements) {
          detailsY = addWrappedText(
            doc,
            measurements,
            leftX,
            detailsY,
            captionTextMaxW,
            14,
            1.2,
            captionMaxY,
          );
        }

        // Price on Request (or price) — left-aligned in right-side column, same baseline as dimensions line.
        const priceY = detailsY - captionLineHeight14; // same baseline as dimensions
        try {
          doc.setFont("AcuminPro", "normal");
        } catch {
          doc.setFont("helvetica", "normal");
        }
        doc.setFontSize(14);
        if (includePrice) {
          const artworkForPrice: any = {
            ...a,
            prices: {
              CHF: (a as any).PriceCHFEnt,
              EUR: (a as any).PriceEUREnt,
              GBP: (a as any).PriceGBPEnt,
              HKD: (a as any).PriceHKDEnt,
              KRW: (a as any).PriceKRWEnt,
              AED: (a as any).PriceAEDEnt,
              USD: (a as any).PriceUSDEnt,
              SGD: (a as any).PriceSGDEnt,
            },
            PriceRef: (a as any).PriceRef,
            CurrencyRef: (a as any).CurrencyRef,
          };
          const { price, currency: displayCurrency } = getArtworkPrice(
            artworkForPrice,
            selectedCurrency,
          );
          const priceText =
            price > 0
              ? formatPriceForPDF(price, displayCurrency as CurrencyCode, false)
              : "Price on Request";
          doc.text(priceText, priceColumnX, priceY, { align: "left" });
        } else {
          doc.text("Price on Request", priceColumnX, priceY, {
            align: "left",
          });
        }

        // --- PAGE 2 (and possibly spanning 2 pages): Artwork information continued — Signature, Provenance, Exhibited, Literature, Certificate ---
        const provenanceText = normalizeWeirdSpacing(
          (a as any).ProvenanceWordingCrm || "",
        );
        const exhibitionsText = normalizeWeirdSpacing(
          a.artwork_exhibited || "",
        );
        const literatureText = normalizeWeirdSpacing(
          a.LiteratureEn || "",
        ).replace(/\r\n?/g, "\n");
        const certificateText = normalizeWeirdSpacing(
          (a as any).CertificateWording || "",
        );
        const signatureText = normalizeWeirdSpacing(a.artwork_signed || "");

        // Page 2 (Artwork information continued) only if we have something to show — otherwise skip the page
        const hasPage2Content =
          signatureText ||
          provenanceText ||
          exhibitionsText ||
          literatureText ||
          certificateText;
        if (hasPage2Content && renderContinuedInfo) {
          doc.addPage();
          addLogo(doc);

          drawAlignmentGuides(doc, "artwork-continued");

          // Continue directly at the same content-start grid used below page headings.
          let y = PDF_LAYOUT.imageTopY + 14 / 72;

          // Artist, title, year block (same as reference: Acumin Pro Bold / Italic / Regular 14pt)
          let artistDatesPage2 = artist;
          if (a.artist_birth_year) {
            artistDatesPage2 += ` (${a.artist_birth_year}${
              a.artist_death_year ? ` - ${a.artist_death_year}` : ""
            })`;
          }
          try {
            doc.setFont("AcuminPro", "bold");
          } catch {
            doc.setFont("helvetica", "bold");
          }
          doc.setFontSize(14);
          y = addWrappedText(doc, artistDatesPage2, leftX, y, maxW, 14);
          if (title) {
            try {
              doc.setFont("AcuminPro", "italic");
            } catch {
              doc.setFont("helvetica", "italic");
            }
            doc.setFontSize(14);
            y = addWrappedText(doc, title, leftX, y, maxW, 14);
          }
          if (dateToDisplay) {
            try {
              doc.setFont("AcuminPro", "normal");
            } catch {
              doc.setFont("helvetica", "normal");
            }
            doc.setFontSize(14);
            y = addWrappedText(doc, dateToDisplay, leftX, y, maxW, 14);
          }
          y += 0.15;

          // Section: label Acumin Pro Bold 14pt, content Acumin Pro Regular 14pt
          const addSection = (label: string, content?: string) => {
            if (!content) return;

            y += 0.2;

            try {
              doc.setFont("AcuminPro", "bold");
            } catch {
              doc.setFont("helvetica", "bold");
            }
            doc.setFontSize(14);
            doc.text(label.toUpperCase(), leftX, y);
            y += (14 * 1.2) / 72 + 0.08;

            try {
              doc.setFont("AcuminPro", "normal");
            } catch {
              doc.setFont("helvetica", "normal");
            }
            doc.setFontSize(14);

            const paragraphs = content
              .split(/\n\n+/)
              .filter((p: string) => p.trim());

            paragraphs.forEach((para: string) => {
              doc.setFont("helvetica", "normal");
              const lines = doc.splitTextToSize(para, maxW);
              try {
                doc.setFont("AcuminPro", "normal");
              } catch {
                doc.setFont("helvetica", "normal");
              }
              doc.setFontSize(14);
              const h = (lines.length * 14 * 1.2) / 72;
              doc.text(lines, leftX, y);
              y += h + 0.12;
            });
            y += 0.1;
          };

          // Spec order: SIGNATURE → Provenance → Exhibited → Literature → Certificate
          addSection("Signature", signatureText);
          addSection("Provenance", provenanceText);
          addSection("Exhibited", exhibitionsText);
          addSection("Literature", literatureText);
          addSection("Certificate", certificateText);
        }

        // --- PAGE 3: Artwork at scale (as per attached PDF) ---
        // const category = (a as any).Category || "";
        // if (
        //   coverLoadedLocal &&
        //   silhouetteLoaded &&
        //   backgroundLoaded &&
        //   (a as any).SizeIncH &&
        //   category !== "Sculpture"
        // ) {
        //   doc.addPage();
        //   addLogo(doc);
        //   drawAlignmentGuides(doc, "margins");

        //   // "Artwork at scale" — use built-in font so title shows on download
        //   doc.setFont("times", "roman");
        //   doc.setFontSize(18);
        //   doc.text("Artwork at scale", leftX, PDF_LAYOUT.artworkDetailTitleY);

        //   // Same side margins as other pages (leftX, maxW) — equal space from left/right
        //   const scaleImageTopY =
        //     PDF_LAYOUT.artworkDetailTitleY + (18 * 1.2) / 72 + 0.2;
        //   const scaleImageAvailH = pageH - scaleImageTopY - bottomM;
        //   const canvasW_pt = 8.3 * 72;
        //   const canvasH_pt = 9.8 * 72;
        //   const SCALE = 2;
        //   const canvas = document.createElement("canvas");
        //   canvas.width = canvasW_pt * SCALE;
        //   canvas.height = canvasH_pt * SCALE;

        //   const imgAspect = canvas.width / canvas.height;
        //   let scaleImageW = maxW;
        //   let scaleImageH = scaleImageW / imgAspect;
        //   // let scaleImageH = 9.8 * (maxW / 8.3);
        //   if (scaleImageH > scaleImageAvailH) {
        //     scaleImageH = scaleImageAvailH;
        //     // scaleImageW = 8.3 * (scaleImageAvailH / 9.8);
        //     scaleImageW = scaleImageH * imgAspect;
        //   }
        //   const scaleImageX = leftX + (maxW - scaleImageW) / 2;
        //   const scaleImageY = scaleImageTopY;

        //   const ctx = canvas.getContext("2d");
        //   if (ctx) {
        //     const floorBottom = canvas.height;
        //     const loadCanvasImage = (src: string): Promise<HTMLImageElement> =>
        //       new Promise((resolve, reject) => {
        //         const img = new Image();
        //         img.crossOrigin = "anonymous";
        //         img.src = src;
        //         img.onload = () => resolve(img);
        //         img.onerror = () => reject(new Error("Image load error"));
        //       });

        //     try {
        //       const backgroundImg = await loadCanvasImage(
        //         backgroundLoaded.dataUrl,
        //       );
        //       const personImg = await loadCanvasImage(silhouetteLoaded.dataUrl);
        //       const artworkHDUrl =
        //         normalize((a as any).main_a6_picture) ||
        //         normalize((a as any).main_a5_picture) ||
        //         normalize(a.main_super_picture_hd) ||
        //         normalize(a.main_picture_hd) ||
        //         normalize(a.main_web_picture);
        //       const perspectiveArtLoaded: any = artworkHDUrl
        //         ? await loadImageData(artworkHDUrl)
        //         : null;
        //       if (perspectiveArtLoaded?.dataUrl) {
        //         const artImg = await loadCanvasImage(
        //           perspectiveArtLoaded.dataUrl,
        //         );
        //         ctx.drawImage(backgroundImg, 0, 0, canvas.width, canvas.height);

        //         const personHeight_in = 9;
        //         const personWidth_in = 8;
        //         const marginOffset_pt = 27;
        //         const personH_pt = personHeight_in * 72 * SCALE;
        //         const personW_pt = personWidth_in * 72 * SCALE;
        //         const personY = floorBottom - personH_pt + marginOffset_pt;
        //         const personX = canvas.width - personW_pt - 0.5 * 72 * SCALE;
        //         ctx.drawImage(
        //           personImg,
        //           personX,
        //           personY,
        //           personW_pt,
        //           personH_pt,
        //         );

        //         const fixedArtWidth_in = 3.5;
        //         const fixedArtHeight_in = 3;
        //         let artW_pt = fixedArtWidth_in * 72 * SCALE;
        //         let artH_pt = fixedArtHeight_in * 72 * SCALE;
        //         const aspectRatio =
        //           coverLoadedLocal.width / coverLoadedLocal.height;
        //         if (artW_pt / artH_pt > aspectRatio)
        //           artW_pt = artH_pt * aspectRatio;
        //         else artH_pt = artW_pt / aspectRatio;

        //         const leftMargin = 0.5 * 72;
        //         const startX = leftMargin;

        //         const removeWhiteBg = (
        //           img: HTMLImageElement,
        //           drawWidth: number,
        //           drawHeight: number,
        //         ): HTMLCanvasElement => {
        //           const tempCanvas = document.createElement("canvas");
        //           tempCanvas.width = drawWidth;
        //           tempCanvas.height = drawHeight;
        //           const tempCtx = tempCanvas.getContext("2d")!;
        //           tempCtx.drawImage(img, 0, 0, drawWidth, drawHeight);
        //           const imageData = tempCtx.getImageData(
        //             0,
        //             0,
        //             drawWidth,
        //             drawHeight,
        //           );
        //           const data = imageData.data;
        //           for (let i = 0; i < data.length; i += 4) {
        //             const r = data[i],
        //               g = data[i + 1],
        //               b = data[i + 2];
        //             if (r > 240 && g > 240 && b > 240) data[i + 3] = 0;
        //           }
        //           tempCtx.putImageData(imageData, 0, 0);
        //           return tempCanvas;
        //         };

        //         const transparentArtCanvas = removeWhiteBg(
        //           artImg,
        //           artW_pt,
        //           artH_pt,
        //         );
        //         ctx.drawImage(
        //           transparentArtCanvas,
        //           startX * SCALE,
        //           (startX + 180) * SCALE,
        //           artW_pt,
        //           artH_pt,
        //         );

        //         const mockupDataUrl = canvas.toDataURL("image/png");
        //         doc.addImage(
        //           mockupDataUrl,
        //           "PNG",
        //           scaleImageX,
        //           scaleImageY,
        //           scaleImageW,
        //           scaleImageH,
        //         );
        //       }
        //     } catch (err) {
        //       console.warn("Artwork at scale mockup error, skipping:", err);
        //     }
        //   }
        // }

        await addImageSectionPage("Artwork at scale", [
          normalize(a.perspective_url),
        ]);

        if (renderMasterMediaPages) {
          await addImageSectionPage("Artwork framed", [
            normalize(a.frame_picture),
          ]);
          await addImageSectionPage("Artwork detail", [
            normalize(a.detail_1_url),
            normalize(a.detail_2_url),
            normalize(a.other_url),
          ]);
          await addImageSectionPage("Artwork signature", [
            normalize(a.signatures_pictures),
          ]);
          await addImageSectionPage("Artwork back", [
            normalize(a.back_pictures),
          ]);
        }

        // --- PAGE 4: Artist biography (biography only once if multiple works by same artist) ---
        // if (renderArtistBio) {
        if (renderArtistBio === true) {
          addArtistBioPage(doc, a);
        }

        // --- PAGES 5 & 6: Artwork detail pictures ---
        // if (detailLoadedLocal && !detailImageRendered) {
        //   doc.addPage();
        //   addLogo(doc);
        //   drawAlignmentGuides(doc, "artwork-detail");

        //   doc.setFont("times", "roman");
        //   doc.setFontSize(18);
        //   doc.text("Artwork detail", leftX, PDF_LAYOUT.artworkDetailTitleY);

        //   const imgLoaded = detailLoadedLocal;
        //   // Homothety only: fit inside box, preserve aspect ratio, center (never stretch)
        //   const startY =
        //     PDF_LAYOUT.artworkDetailTitleY +
        //     (18 * 1.2) / 72 +
        //     PDF_LAYOUT.artworkDetailSpaceAfterTitle;
        //   const boxW = maxW;
        //   const boxH = pageH - startY - bottomM;
        //   const imgAspect = (imgLoaded.width || 1) / (imgLoaded.height || 1);
        //   const boxAspect = boxW / boxH;
        //   let finalW: number;
        //   let finalH: number;
        //   if (imgAspect > boxAspect) {
        //     finalW = boxW;
        //     finalH = boxW / imgAspect;
        //   } else {
        //     finalH = boxH;
        //     finalW = boxH * imgAspect;
        //   }
        //   const imgX = leftX + (boxW - finalW) / 2;
        //   const imgY = startY + (boxH - finalH) / 2;
        //   doc.addImage(
        //     imgLoaded.dataUrl,
        //     imgLoaded.format,
        //     imgX,
        //     imgY,
        //     finalW,
        //     finalH,
        //     undefined,
        //     "FAST",
        //   );
        // }

        // Helper to render extra detail pages (each with "Artwork detail" title)
        // const renderExtraDetailPage = (imgLoaded: any) => {
        //   if (!imgLoaded) return;
        //   doc.addPage();
        //   addLogo(doc);
        //   drawAlignmentGuides(doc, "artwork-detail");

        //   doc.setFont("times", "roman");
        //   doc.setFontSize(18);
        //   doc.text("Artwork detail", leftX, PDF_LAYOUT.artworkDetailTitleY);

        //   const startY =
        //     PDF_LAYOUT.artworkDetailTitleY +
        //     (18 * 1.2) / 72 +
        //     PDF_LAYOUT.artworkDetailSpaceAfterTitle;
        //   const boxW = maxW;
        //   const boxH = pageH - startY - bottomM;
        //   const imgAspect = (imgLoaded.width || 1) / (imgLoaded.height || 1);
        //   const boxAspect = boxW / boxH;
        //   let finalW: number;
        //   let finalH: number;
        //   if (imgAspect > boxAspect) {
        //     finalW = boxW;
        //     finalH = boxW / imgAspect;
        //   } else {
        //     finalH = boxH;
        //     finalW = boxH * imgAspect;
        //   }
        //   const imgX = leftX + (boxW - finalW) / 2;
        //   const imgY = startY + (boxH - finalH) / 2;
        //   doc.addImage(
        //     imgLoaded.dataUrl,
        //     imgLoaded.format,
        //     imgX,
        //     imgY,
        //     finalW,
        //     finalH,
        //     undefined,
        //     "FAST",
        //   );
        // };

        // if (det1Loaded && det1Url !== coverUrl && det1Url !== detailUrl) {
        //   renderExtraDetailPage(det1Loaded);
        // }

        // if (
        //   det2Loaded &&
        //   det2Url !== coverUrl &&
        //   det2Url !== detailUrl &&
        //   det2Url !== det1Url
        // ) {
        //   renderExtraDetailPage(det2Loaded);
        // }
      }; // end addArtworkPages
      if (mode === "multiple") {
         renderedArtists.clear();  // one bio per artist in this combined PDF
        // const artistGroups = new Map<string, any[]>();
        // for (const a of artworksFull) {
        //   if (!a) continue;
        //   const { artistKey } = getArtistMeta(a);
        //   const list = artistGroups.get(artistKey) || [];
        //   list.push(a);
        //   artistGroups.set(artistKey, list);
        // }
        const artistGroups = new Map<string, any[]>();

for (const a of artworksFull) {
  if (!a) continue;

  const { artistKey } = getArtistMeta(a);
  const bioText = cleanText(a?.artist_bio || "");
  const collectionsText = cleanText(a?.selected_collections || "");

  // 🔥 group by artist + bio content
  const groupKey = `${artistKey}__${bioText}__${collectionsText}`;

  const list = artistGroups.get(groupKey) || [];
  list.push(a);
  artistGroups.set(groupKey, list);
}

        for (const group of artistGroups.values()) {
          for (const a of group) {
            await addArtworkPages(doc, a, false);
            await yieldToUI();
          }
          const lastArtworkOfArtist = group[group.length - 1];
          if (lastArtworkOfArtist) {
            addArtistBioPage(doc, lastArtworkOfArtist);
            await yieldToUI();
          }
        }
        const fileName = `proposal_${artworksFull.length}_artworks.pdf`;
        const blob = doc.output("blob"); // single blob for both view and download so content is identical

        if (singlePdfReadyHandler) {
          await Promise.resolve(singlePdfReadyHandler(blob, artworksFull));
        } else if (action === "view") {
          window.open(URL.createObjectURL(blob), "_blank");
        } else if (action === "attach") {
          const b64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve(String(reader.result).split(",")[1] || "");
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          setAttachments((prev) => [
            ...prev,
            { name: fileName, content: b64, source: "proposal" },
          ]);
          setIsPdfReady(true);
        } else {
          await syncDownloadPdfsToCrmIfNeeded([{ blob, fileName }]);
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
        if (skippedNoCover.length > 0) {
          alert(formatSkippedArtworksMessage(skippedNoCover));
        }
        return;
      }

      // 🔥 SINGLE → EACH ARTWORK SEPARATE PDF (each file gets that artwork's bio and same headings as view)
      if (mode === "single") {
        const proposalPdfAttachments: { blob: Blob; fileName: string }[] = [];
        for (const a of artworksFull) {
          if (!a) continue;

          renderedArtists.clear();  // each single PDF gets its own artist bio
          const singleDoc = new jsPDF({
            unit: "in",
            format: "a4",
            orientation: "portrait",
          });
          await registerAcuminProFonts(singleDoc);
          singleDoc.deletePage(1);

          await addArtworkPages(singleDoc, a, true);

          const safeTitle = useMockData
            ? "test_proposal_sample"
            : (a.name || "artwork").replace(/[^a-z0-9]/gi, "_").toLowerCase();

          const blob = singleDoc.output("blob");
          if (singlePdfReadyHandler) {
            await Promise.resolve(singlePdfReadyHandler(blob, a));
          } else if (action === "view") {
            window.open(URL.createObjectURL(blob), "_blank");
          } else if (action === "attach") {
            const b64 = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () =>
                resolve(String(reader.result).split(",")[1] || "");
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
            setAttachments((prev) => [
              ...prev,
              {
                name: `${safeTitle}.pdf`,
                content: b64,
                source: "proposal",
              },
            ]);
          } else {
            const singleFileName = `${safeTitle}.pdf`;
            proposalPdfAttachments.push({ blob, fileName: singleFileName });
            // Manual blob download to ensure font rendering consistency
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = singleFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }

          await yieldToUI();
        }

        await syncDownloadPdfsToCrmIfNeeded(proposalPdfAttachments);

        setShowPDFOptionsModal(false);
        setIsPdfReady(true);
        if (skippedNoCover.length > 0) {
          alert(formatSkippedArtworksMessage(skippedNoCover));
        }
        return;
      }

      // Iterate artworks and render
      // let first = true;
      // for (const a of artworksFull) {
      //   if (!a) {
      //     setDownloadDone((v) => v + 1);
      //     continue;
      //   }
      //   if (!first) {
      //     // nothing special; addArtworkPages creates pages as needed
      //   }
      //   first = false;
      //   await addArtworkPages(a, includePrice);
      //   setDownloadDone((v) => v + 1);
      //   await yieldToUI();
      // }
      // const fileName =
      //   selectedArtworks.length === 1
      //     ? `${selectedArtworks[0].IdName || selectedArtworks[0].id}.pdf`
      //     : `proposal_${selectedArtworks.length}_artworks.pdf`;

      // const blob = doc.output("blob");

      // if (!blob || typeof blob.size !== "number" || blob.size < 2048) {
      //   alert("Failed to generate a valid PDF. Please try again.");
      //   return;
      // }

      // doc.save(fileName);

      try {
        const backendUrl =
          (odooClient as any)["backendURL"] ||
          process.env.REACT_APP_BACKEND_URL ||
          "";

        if (backendUrl) {
          fetch(`${backendUrl}/api/pdf/activity`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              user_id: user?.id,
              user_name: `${user?.firstName || ""} ${
                user?.lastName || ""
              }`.trim(),
              artwork_ids: artworksFull.map((a) => a?.id).filter(Boolean),
              artwork_names: artworksFull.map((a) => a?.name).filter(Boolean),
              source: "proposal",
              meta: {
                email: user.email || "",
                role: user.role || "user",
              },
            }),
          });
        }
      } catch (_) {
        /* ignore attachment conversion errors */
      }
    } catch (e) {
      console.error("PDF ERROR RAW →", e);
      console.error("PDF ERROR STRING →", String(e));
      if (e instanceof Error) {
        console.error("PDF ERROR MESSAGE →", e.message);
        console.error("PDF ERROR STACK →", e.stack);
        alert(e.message);
      } else {
        alert(JSON.stringify(e));
      }
    } finally {
      if (!suppressDownloadLoading) {
        setDownloadingPDF(false);
      }
    }
  };

  // When bulk download: auto-run PDF generation and call onSinglePdfReady (no UI options).
  useEffect(() => {
    if (
      !isOpen ||
      !autoDownloadSingle ||
      !onSinglePdfReady ||
      selectedArtworks.length === 0
    )
      return;

    const mode = selectedArtworks.length > 1 ? "multiple" : "single";
    handleDownloadProposalPDF(false, mode, "download", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, autoDownloadSingle, onSinglePdfReady, selectedArtworks]);


  const autoAttachProposalPDF = async (
    includePrice: boolean = false,
    mode: "single" | "multiple",
  ) => {
    // Reuse the main PDF generator for consistency
    return handleDownloadProposalPDF(includePrice, mode, "attach");
  };

  const buildArtworksForProposalNote = () =>
    selectedArtworks.map((sa: any) => ({
      id: sa.id,
      name: sa.title || sa.name,
      IdName: sa.IdName,
    }));

  const syncInterestsAfterProposal = async (
    artworksLike: any[] | null | undefined,
    clientForSync: UserOption | null | undefined,
  ) => {
    if (!artworksLike?.length) return;
    if (clientForSync?.id == null) return;
    const cid = Number(clientForSync.id);
    if (!Number.isFinite(cid) || cid <= 0) return;
    try {
      const { default: odooClient } = await import("../services/odooClient");
      const { syncArtistsToContactsFromArtworkLike } = await import(
        "../utils/contactInterestArtist"
      );
      await syncArtistsToContactsFromArtworkLike(odooClient, artworksLike, [
        { id: cid, _model: clientForSync._model },
      ]);
      onContactInterestsSynced?.({
        contactId: cid,
        _model: clientForSync._model,
      });
    } catch (err) {
      console.warn("syncInterestsAfterProposal failed (non-fatal):", err);
    }
  };

  /**
   * Push one short row on the Offers > Timeline tab (`artgallery.opportunity.timeline`).
   *
   * - Lead id resolution: explicit override > `crmLeadIdProp` (modal opened from Offers card).
   *   No lead resolved => silently skip (e.g. proposal preview before any CRM sync).
   * - `x_user_id` is stamped server-side from the Odoo session; we only ship the message.
   * - Fire-and-forget: never blocks the calling action.
  */

  const appendOpportunityTimelineEntry = async (
    action: "download" | "webView" | "copyPdfLink" | "sendMail",
    leadIdOverride?: number | null,
  ): Promise<void> => {
    let lid: number | null = null;
    if (leadIdOverride != null && Number.isFinite(Number(leadIdOverride)) && Number(leadIdOverride) > 0) {
      lid = Number(leadIdOverride);
    } else if (
      crmLeadIdProp != null &&
      Number.isFinite(Number(crmLeadIdProp)) &&
      Number(crmLeadIdProp) > 0
    ) {
      lid = Number(crmLeadIdProp);
    }
    if (lid == null) return;

    // Single source of truth for action → message. Returns `null` for actions
    // already auto-logged by the backend (e.g. `download` → backend's
    // `crm.lead.create()` override stamps "Offer created" on new leads).
    const { timelineMessageForProposalAction } = await import(
      "../utils/proposalCrmPipeline"
    );
    const message = timelineMessageForProposalAction(action);
    if (!message) return;
    try {
      const { default: odooClient } = await import("../services/odooClient");
      await odooClient.appendCrmLeadOpportunityTimeline(lid, message);
    } catch (err) {
      console.warn("appendOpportunityTimelineEntry failed (non-fatal):", err);
    }
  };

  const postProposalClientNote = async (
    action: ProposalNoteAction,
    artworksForNote: any[],
    clientOverride?: UserOption | null,
    artworksLikeForInterests?: any[] | null,
    /**
     * Public URL produced by the action (web-view URL for "webView",
     * first PDF share URL for "copyPdfLink"). When provided, the verb
     * phrase in the note becomes an anchor tag — the ContactDetail
     * renderer only makes it clickable for the authoring user.
     */
    shareLink?: string | null,
  ) => {
    const client = clientOverride ?? proposalPdfClient;
    if (!client?.id || !artworksForNote?.length) return;
    const list = artworksForNote.filter((a) => a?.id);
    if (!list.length) return;

    const userName =
      `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
      user?.email ||
      "User";
    const origin = window.location.origin;
    const artworkLinksHtml = list
      .map((a) => {
        const id = Number(a.id);
        const label = escapeProposalNoteText(
          String((a as any).IdName || a.name || a.title || `Artwork ${id}`),
        );
        return `<a href="${origin}/artworks/${id}">${label}</a>`;
      })
      .join(", ");
    const noteHtml = buildProposalNoteBody(userName, action, artworkLinksHtml, {
      shareLink,
    });

    try {
      const { default: odooClient } = await import("../services/odooClient");
      const noteModel =
        client._model === "crm.prospect"
          ? "crm.prospect"
          : "res.partner";
      await odooClient.postInternalNote(
        noteModel,
        Number(client.id),
        noteHtml,
      );
    } catch (err) {
      console.warn("postInternalNote failed (non-fatal):", err);
    }
    await syncInterestsAfterProposal(
      artworksLikeForInterests ?? selectedArtworks,
      client,
    );
  };

  const fetchFullArtworksForCrmPipeline = async (): Promise<any[]> => {
    const { default: odooClient } = await import("../services/odooClient");
    const fetchTasks = selectedArtworks.map((sa: any) => async () => {
      try {
        return await odooClient.getArtworkById(parseInt(String(sa.id), 10));
      } catch {
        return null;
      }
    });
    return (await withConcurrency(fetchTasks, 5)).filter(Boolean);
  };

  const ensureProposalCrmPipelineForClient = async (
    client: UserOption | null | undefined,
    artworksFull: any[],
    pdfFiles?: { blob: Blob; fileName: string }[],
    timelineAction?: "download" | "webView" | "copyPdfLink" | "sendMail" | null,
  ) => {
    if (!client?.id) return;
    const cid = Number(client.id);
    if (!Number.isFinite(cid)) return;
    const { default: odooClient } = await import("../services/odooClient");
    const { syncProposalCrmPipeline, timelineMessageForProposalAction } =
      await import("../utils/proposalCrmPipeline");
    // `skipPriceHistory: true` — this helper runs on PDF download / link copy
    // previews which can fire multiple times before a single send. We still
    // ensure the lead exists + its `expected_revenue` is up to date and attach
    // the preview PDFs, but only the actual send button (below in the Send
    // handler) writes a `Proposal sync` row so the Offers Price History tab
    // reflects one entry per send instead of one per preview action.
    //
    // `timelineAction` is forwarded to the pipeline so the timeline row is
    // appended at the same moment the lead is resolved (covers brand-new
    // leads created mid-flow without the caller needing to capture the id).
    const timelineMessage = timelineAction
      ? timelineMessageForProposalAction(timelineAction)
      : null;
    return syncProposalCrmPipeline(odooClient, {
      clientId: cid,
      clientModel: client._model,
      artworksFull,
      selectedCurrency,
      pdfFiles: pdfFiles ?? [],
      skipPriceHistory: true,
      timelineMessage,
    });
  };

  const handleCopyProposalLink = async (includePrice: boolean = false) => {
    if (selectedArtworks.length === 0) return;

    // If HTML proposal link is already generated, copy directly
    if (shareUrl && shareUrl.includes("/proposal-view/")) {
      const copied = await copyToClipboardSafe(shareUrl);
      setLinkCopied(copied);
      setTimeout(() => setLinkCopied(false), 2500);
      let webViewLeadId: number | null = null;
      if (proposalPdfClient?.id) {
        const crmPdfAttachments: { blob: Blob; fileName: string }[] = [];
        await handleDownloadProposalPDF(
          includePrice,
          "single",
          "download",
          false,
          async (blob: Blob, artworkForPdf: any) => {
            const safeTitle = (artworkForPdf?.name || "artwork")
              .replace(/[^a-z0-9]/gi, "_")
              .toLowerCase();
            crmPdfAttachments.push({ blob, fileName: `${safeTitle}.pdf` });
          },
          { suppressDownloadLoading: true },
        );
        const artworksForCrm = await fetchFullArtworksForCrmPipeline();
        const lid = await ensureProposalCrmPipelineForClient(
          proposalPdfClient,
          artworksForCrm,
          crmPdfAttachments,
          "webView",
        );
        if (lid != null && Number.isFinite(Number(lid)) && Number(lid) > 0) {
          webViewLeadId = Number(lid);
        }
      }
      await postProposalClientNote(
        "webView",
        buildArtworksForProposalNote(),
        undefined,
        selectedArtworks,
        shareUrl,
      );
      // Modal opened from the Offers card: pipeline above wasn't called because
      // there was no fresh client to sync. Fall back to the helper which uses
      // `crmLeadIdProp` so the existing lead still gets a "Web view shared"
      // timeline row. When the pipeline did run, `webViewLeadId` is set and we
      // skip the helper to avoid double-logging the same click.
      if (webViewLeadId == null) {
        await appendOpportunityTimelineEntry("webView");
      }
      {
        const { default: odooClient } = await import(
          "../services/odooClient"
        );
        await odooClient.recordArtworkProposalAction(
          selectedArtworks.map((sa: any) => sa?.id),
          proposalPdfClient,
        );
      }
      return;
    }

    setGeneratingLink(true);
    setLinkCopied(false);

    try {
      const { default: odooClient } = await import("../services/odooClient");
      const fetchTasks = selectedArtworks.map((sa) => async () => {
        try {
          return await odooClient.getArtworkById(parseInt(sa.id));
        } catch {
          return null;
        }
      });
      const artworksFull = await withConcurrency(fetchTasks, 5);

      const normalize = (v: any): string | undefined => {
        if (typeof v !== "string") return undefined;
        const t = v.trim();
        if (!t || t.toLowerCase() === "false" || t.toLowerCase() === "null")
          return undefined;
        return t;
      };
      // const backendUrl = (() => {
      //   const env = String(process.env.REACT_APP_BACKEND_URL || "").trim();
      //   if (env && env !== "undefined") return env;
      //   const host = window.location.hostname;
      //   if (host === "localhost" || host === "127.0.0.1") return "http://localhost:5000";
      //   return `${window.location.protocol}//${window.location.host}`;
      // })();
      const backendUrl =
        odooClient["backendURL"] || process.env.REACT_APP_BACKEND_URL || "";
      const odooBaseURL = (odooClient as any)["baseURL"] || "";
      const singlePdfUrlsByArtworkId = new Map<string, string>();
      const crmPdfAttachmentsForWebView: { blob: Blob; fileName: string }[] =
        [];

      if (backendUrl) {
        await handleDownloadProposalPDF(
          includePrice,
          "single",
          "download",
          false,
          async (blob: Blob, artworkForPdf: any) => {
            try {
              const uploadRes = await fetch(`${backendUrl}/api/admin/pdf/upload`, {
                method: "POST",
                headers: { "Content-Type": "application/pdf" },
                body: blob,
              });
              if (!uploadRes.ok) return;
              const uploadData = await uploadRes.json();
              const uploadedPdfUrl = String(uploadData?.url || "").trim();
              const artworkId = String(artworkForPdf?.id ?? "").trim();
              if (uploadedPdfUrl && artworkId) {
                singlePdfUrlsByArtworkId.set(artworkId, uploadedPdfUrl);
              }
              const safeTitle = (artworkForPdf?.name || "artwork")
                .replace(/[^a-z0-9]/gi, "_")
                .toLowerCase();
              crmPdfAttachmentsForWebView.push({
                blob,
                fileName: `${safeTitle}.pdf`,
              });
            } catch {
              // Keep generating proposal even if one PDF upload fails.
            }
          },
          { suppressDownloadLoading: true },
        );
      }

      const buildDimensions = (a: any) => {
        if (a?.SizeH && a?.SizeL && a?.SizeIncH && a?.SizeIncL) {
          return `${a.SizeH} x ${a.SizeL} cm | ${a.SizeIncH} x ${a.SizeIncL} in`;
        }
        if (a?.SizeH && a?.SizeL) return `${a.SizeH} x ${a.SizeL} cm`;
        if (a?.SizeIncH && a?.SizeIncL) return `${a.SizeIncH} x ${a.SizeIncL} in`;
        return "";
      };

      const splitUrls = (value: any): string[] => {
        const v = normalize(value);
        if (!v) return [];
        if (v.startsWith("data:")) return [v];
        if ((v.startsWith("[") && v.endsWith("]")) || (v.startsWith("{") && v.endsWith("}"))) {
          try {
            const parsed = JSON.parse(v);
            if (Array.isArray(parsed)) {
              return parsed
                .map((item) => normalize(item))
                .filter((item): item is string => Boolean(item));
            }
          } catch {
            // Fall back to string splitting.
          }
        }
        return v
          .split(/[\n,;|]+/)
          .map((s) => normalize(s))
          .filter((s): s is string => Boolean(s));
      };

      const uniqueUrls = (items: string[]) => {
        const seen = new Set<string>();
        return items.filter((item) => {
          if (seen.has(item)) return false;
          seen.add(item);
          return true;
        });
      };

      const artworksPayload = artworksFull
        .filter(Boolean)
        .map((a: any) => {
          const cover =
            getPrimaryArtworkImageUrl(a) ||
            normalize(a.main_hd_web_picture) ||
            normalize(a.detail_1_url) ||
            normalize(a.other_url) ||
            normalize((a as any).main_a6_picture) ||
            normalize((a as any).main_a5_picture) ||
            normalize(a.perspective_url);

          const artist = Array.isArray(a?.res_artist_id)
            ? a?.res_artist_id[1] || "Unknown Artist"
            : a?.res_artist_id || "Unknown Artist";
          const year = a?.artwork_year ? String(a.artwork_year) : "";

          const artworkForPrice: any = {
            ...a,
            prices: {
              CHF: (a as any).PriceCHFEnt,
              EUR: (a as any).PriceEUREnt,
              GBP: (a as any).PriceGBPEnt,
              HKD: (a as any).PriceHKDEnt,
              KRW: (a as any).PriceKRWEnt,
              AED: (a as any).PriceAEDEnt,
              USD: (a as any).PriceUSDEnt,
              SGD: (a as any).PriceSGDEnt,
            },
            PriceRef: (a as any).PriceRef,
            CurrencyRef: (a as any).CurrencyRef,
          };
          const { price, currency } = getArtworkPrice(
            artworkForPrice,
            selectedCurrency,
          );

          const hasManualMainPicture = Boolean(
            (a as any).NewMainLR || (a as any).NewMainHR,
          );
          const imageUrls = uniqueUrls([
            ...splitUrls((a as any).NewMainLR),
            ...splitUrls((a as any).NewMainHR),
            ...(hasManualMainPicture
              ? []
              : [
                  ...splitUrls((a as any).main_super_picture_hd),
                  ...splitUrls((a as any).main_picture_hd),
                  ...splitUrls((a as any).main_web_picture),
                  ...splitUrls((a as any).main_hd_web_picture),
                  ...splitUrls((a as any).main_a6_picture),
                ]),
            ...splitUrls((a as any).detail_1_url),
            ...splitUrls((a as any).detail_2_url),
            ...splitUrls((a as any).perspective_url),
            ...splitUrls((a as any).frame_picture),
            ...splitUrls((a as any).back_pictures),
            ...splitUrls((a as any).signatures_pictures),
            ...splitUrls((a as any).other_url),
          ]);

          const videoUrls = uniqueUrls([
            ...splitUrls((a as any).VideoPresentationUrl1),
            ...splitUrls((a as any).VideoPresentationUrl2),
            ...splitUrls((a as any).VideoDetailUrl1),
            ...splitUrls((a as any).VideoDetailUrl2),
            ...splitUrls((a as any).VideoInstallationUrl1),
            ...splitUrls((a as any).VideoInstallationUrl2),
            ...splitUrls((a as any).VideoTimelapseUrl1),
            ...splitUrls((a as any).VideoTimelapseUrl2),
            ...splitUrls((a as any).VideoDocumentaryUrl1),
            ...splitUrls((a as any).VideoDocumentaryUrl2),
            ...splitUrls((a as any).VideoInterviewUrl1),
            ...splitUrls((a as any).VideoInterviewUrl2),
          ]);

          return {
            id: String(a?.id ?? ""),
            title: String(a?.name || "").trim(),
            artist,
            year,
            medium: String(a?.Medium || a?.medium || "").trim(),
            dimensions: buildDimensions(a),
            price:
              includePrice && price > 0
                ? formatPriceForPDF(price, currency as CurrencyCode, false)
                : "Price on Request",
            mainImageUrl: cover || "",
            imageUrls,
            videoUrls,
            NewMainLR: normalize((a as any).NewMainLR) || "",
            NewMainHR: normalize((a as any).NewMainHR) || "",
            mainSuperPictureHd: normalize((a as any).main_super_picture_hd) || "",
            mainPictureHd: normalize((a as any).main_picture_hd) || "",
            mainWebPicture: normalize((a as any).main_web_picture) || "",
            mainHdWebPicture: normalize((a as any).main_hd_web_picture) || "",
            mainA6Picture: normalize((a as any).main_a6_picture) || "",
            detail1Url: normalize((a as any).detail_1_url) || "",
            detail2Url: normalize((a as any).detail_2_url) || "",
            perspectiveUrl: normalize((a as any).perspective_url) || "",
            framePicture: normalize((a as any).frame_picture) || "",
            backPictures: normalize((a as any).back_pictures) || "",
            signaturesPictures: normalize((a as any).signatures_pictures) || "",
            otherUrl: normalize((a as any).other_url) || "",
            videoPresentationUrl1: normalize((a as any).VideoPresentationUrl1) || "",
            videoPresentationUrl2: normalize((a as any).VideoPresentationUrl2) || "",
            videoDetailUrl1: normalize((a as any).VideoDetailUrl1) || "",
            videoDetailUrl2: normalize((a as any).VideoDetailUrl2) || "",
            videoInstallationUrl1: normalize((a as any).VideoInstallationUrl1) || "",
            videoInstallationUrl2: normalize((a as any).VideoInstallationUrl2) || "",
            videoTimelapseUrl1: normalize((a as any).VideoTimelapseUrl1) || "",
            videoTimelapseUrl2: normalize((a as any).VideoTimelapseUrl2) || "",
            videoDocumentaryUrl1: normalize((a as any).VideoDocumentaryUrl1) || "",
            videoDocumentaryUrl2: normalize((a as any).VideoDocumentaryUrl2) || "",
            videoInterviewUrl1: normalize((a as any).VideoInterviewUrl1) || "",
            videoInterviewUrl2: normalize((a as any).VideoInterviewUrl2) || "",
            pdfUrl: singlePdfUrlsByArtworkId.get(String(a?.id ?? "")) || "",
            certificate:normalizeWeirdSpacing((a as any)?.CertificateWording || ""),
            bio: normalizeWeirdSpacing(a?.artist_bio || ""),
            provenance: normalizeWeirdSpacing((a as any)?.ProvenanceWordingCrm || ""),
            exhibitions: normalizeWeirdSpacing(a?.artwork_exhibited || ""),
            literature: normalizeWeirdSpacing(a?.LiteratureEn || "").replace(
              /\r\n?/g,
              "\n",
            ),
            seller: sharedProposalSeller,
          };
        });

        console.log("pdfUrl", artworksFull)

      if (artworksPayload.length === 0) {
        throw new Error("No artworks available to generate proposal link");
      }

      const payload = {
        includePrice,
        selectedCurrency,
        viewMode: artworksPayload.length === 1 ? "media-first" : undefined,
        singleArtwork: artworksPayload.length === 1,
        hideBackButton: artworksPayload.length === 1,
        odooBaseUrl: odooBaseURL,
        generatedBy: sharedProposalSeller,
        artworks: artworksPayload,
      };

      let uploadedUrl: string | null = null;
      try {
        const res = await fetch(`${backendUrl}/api/odoo/proposal-html/upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.id) {
            uploadedUrl = `${proposalPublicBase}/proposal-view/${data.id}`;
          } else if (data?.url) {
            uploadedUrl = data.url;
          }
        }
      } catch (_) {}

      if (uploadedUrl) {
        const link = uploadedUrl as string;
        setShareUrl(link);
        // setFullTextContent((prev) => {
        //   if (!prev || !prev.trim()) return prev;
        //   if (prev.includes(link)) return prev;
        //   return `${prev.trim()}\n\n${link}`;
        // });
         setFullTextContent((prev) => {
          // When user has not manually edited preview yet, `prev` is null.
          // Build from currently visible template text, then append link.
          const currentVisibleText =
            prev && prev.trim()
              ? prev
              : `${customGreeting || "Dear"} ${
                  greetingName || "Client"
                },\n\n${messageContent}${shareUrl ? `\n\n${shareUrl}` : ""}`.trim();

          if (!currentVisibleText) return link;
          if (currentVisibleText.includes(link)) return currentVisibleText;
          return `${currentVisibleText.trim()}\n\n${link}`;
        });
        const copied = await copyToClipboardSafe(link);
        setLinkCopied(copied);
        setTimeout(() => setLinkCopied(false), 2500);
        let webViewLeadId: number | null = null;
        if (proposalPdfClient?.id) {
          const lid = await ensureProposalCrmPipelineForClient(
            proposalPdfClient,
            artworksFull,
            crmPdfAttachmentsForWebView,
            "webView",
          );
          if (lid != null && Number.isFinite(Number(lid)) && Number(lid) > 0) {
            webViewLeadId = Number(lid);
          }
        }
        await postProposalClientNote(
          "webView",
          buildArtworksForProposalNote(),
          undefined,
          selectedArtworks,
          link,
        );
        // Pipeline writes the timeline when a client is present; fall back to
        // the helper (uses `crmLeadIdProp`) only when no pipeline ran so we
        // never double-log a single click.
        if (webViewLeadId == null) {
          await appendOpportunityTimelineEntry("webView");
        }
        await odooClient.recordArtworkProposalAction(
          selectedArtworks.map((sa: any) => sa?.id),
          proposalPdfClient,
        );
      }
    } catch (e) {
      console.error("Proposal link generation failed:", e);
      alert("Unable to generate proposal link. Please try again.");
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyProposalPdfLink = async (
    includePrice: boolean = false,
    mode: "single" | "multiple",
  ) => {
    if (selectedArtworks.length === 0) return;

    setGeneratingPdfLink(true);
    setPdfLinkCopied(false);

    
    try {
      const { default: odooClient } = await import("../services/odooClient");
      // const backendUrl = (() => {
      //   const env = String(process.env.REACT_APP_BACKEND_URL || "").trim();
      //   if (env && env !== "undefined") return env;
      //   const host = window.location.hostname;
      //   if (host === "localhost" || host === "127.0.0.1") return "http://localhost:5000";
      //   return `${window.location.protocol}//${window.location.host}`;
      // })();

      const backendUrl =
        odooClient["backendURL"] || process.env.REACT_APP_BACKEND_URL || "";

      if (!backendUrl) {
        throw new Error("Backend URL not configured");
      }

      const uploadedUrls: string[] = [];
      const crmPdfAttachmentsForPdfLink: { blob: Blob; fileName: string }[] =
        [];

      await handleDownloadProposalPDF(
        includePrice,
        mode,
        "download",
        false,
        async (blob: Blob, second?: any) => {
          const uploadRes = await fetch(`${backendUrl}/api/admin/pdf/upload`, {
            method: "POST",
            headers: { "Content-Type": "application/pdf" },
            body: blob,
          });
          if (!uploadRes.ok) {
            throw new Error("Upload failed");
          }
          const uploadData = await uploadRes.json();
          const url = String(uploadData?.url || "").trim();
          if (!url) {
            throw new Error("No URL returned");
          }
          uploadedUrls.push(url);
          const fileName = Array.isArray(second)
            ? `proposal_${second.length}_artworks.pdf`
            : `${String(second?.name || "artwork")
                .replace(/[^a-z0-9]/gi, "_")
                .toLowerCase()}.pdf`;
          crmPdfAttachmentsForPdfLink.push({ blob, fileName });
        },
        { suppressDownloadLoading: true },
      );

      if (uploadedUrls.length === 0) {
        throw new Error("Failed to generate PDF link");
      }

      const pdfLinksText = uploadedUrls.join("\n");
      setPdfShareUrl(pdfLinksText);
      const copied = await copyToClipboardSafe(pdfLinksText);
      setPdfLinkCopied(copied);
      setTimeout(() => setPdfLinkCopied(false), 2500);
      let copyPdfLinkLeadId: number | null = null;
      if (proposalPdfClient?.id) {
        const artworksForCrm = await fetchFullArtworksForCrmPipeline();
        const lid = await ensureProposalCrmPipelineForClient(
          proposalPdfClient,
          artworksForCrm,
          crmPdfAttachmentsForPdfLink,
          "copyPdfLink",
        );
        if (lid != null && Number.isFinite(Number(lid)) && Number(lid) > 0) {
          copyPdfLinkLeadId = Number(lid);
        }
      }
      // Pipeline writes the timeline when a client is present; fall back to
      // `crmLeadIdProp` (Offers card flow) only when no pipeline ran so we
      // never double-log a single click.
      if (copyPdfLinkLeadId == null) {
        await appendOpportunityTimelineEntry("copyPdfLink");
      }
      await postProposalClientNote(
        "copyPdfLink",
        buildArtworksForProposalNote(),
        undefined,
        selectedArtworks,
        uploadedUrls[0] ?? null,
      );
      await odooClient.recordArtworkProposalAction(
        selectedArtworks.map((sa: any) => sa?.id),
        proposalPdfClient,
      );
    } catch (e) {
      console.error("Proposal PDF link generation failed:", e);
      alert("Unable to generate PDF link. Please try again.");
    } finally {
      setGeneratingPdfLink(false);
    }
  };

  const hasPdfOrLink = useMemo(() => {
    return (
      attachments.length > 0 ||
      (shareUrl && shareUrl.trim().length > 0) ||
      (pdfShareUrl && pdfShareUrl.trim().length > 0)
    );
  }, [attachments.length, shareUrl, pdfShareUrl]);

  const pdfShareLinks = useMemo(
    () =>
      (pdfShareUrl || "")
        .split(/\r?\n/)
        .map((link) => link.trim())
        .filter(Boolean),
    [pdfShareUrl],
  );
  const isProposalActionBusy =
    downloadingPDF || generatingLink || generatingPdfLink;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900 bg-opacity-50"
        onClick={onClose}
      />

      <style>
        {`

      .nice-scrollbar { scrollbar-width: thin; scrollbar-color: #9CA3AF #E5E7EB; }
      .nice-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
      .nice-scrollbar::-webkit-scrollbar-track { background: #E5E7EB; border-radius: 8px; }
      .nice-scrollbar::-webkit-scrollbar-thumb { background-color: #9CA3AF; border-radius: 8px; border: 2px solid #E5E7EB; }
      .nice-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #6B7280; }
    `}

      </style>


      <div
        className="relative mx-2 w-full max-w-[min(1450px,calc(100vw-1rem))] max-h-[90vh] overflow-hidden overflow-y-auto rounded-2xl bg-white p-4 shadow-2xl sm:mx-4 sm:p-6 lg:p-[30px]"
        style={{
          minHeight: `${Math.min(
            PROPOSAL_MODAL_HEIGHT,
            window.innerHeight * 0.9,
          )}px`,
        }}
      >
        {/* Header */}

        <div className="relative  py-4 border-b">

          {/* Close button in top right */}
          <button
            onClick={onClose}
            className="absolute top-5 right-4 sm:right-[0.1rem] text-gray-400 hover:text-gray-600 z-10"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>


          <div className="flex flex-col gap-3 pr-10 sm:flex-row sm:items-center sm:justify-between sm:gap-0 sm:pr-16">
            <h2 className="text-lg font-bold text-gray-900 sm:text-2xl">
              Artwork(s) Proposal
            </h2>
            {/*
              Top action toolbar (Web view / Copy PDF link / Download pdf).
              Hidden when the modal runs in list-mode — there the artworks
              are pre-determined by the caller, the user can't change them,
              and the caller already pre-attaches the right output (PDF
              blob for external recipients, dashboard link for internal).
            */}
            {!hideArtworkPicker && (
            <div className="flex w-full flex-row flex-wrap items-center justify-center gap-2 sm:w-auto sm:flex-nowrap sm:justify-end">
              <button
                onClick={() => {
                  if (isProposalActionBusy) return;
                  setPdfModalAction("webView");
                  setShowPDFOptionsModal(true);
                }}
                disabled={isProposalActionBusy || selectedArtworks.length === 0}
                className={`inline-flex shrink-0 items-center justify-center rounded-lg border px-2.5 py-1.5 text-xs transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:py-2 sm:text-sm ${
                  linkCopied
                    ? "text-green-600 bg-green-50 border-green-200 hover:bg-green-100"
                    : "text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100"
                }`}
              >
                {generatingLink ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Generating…
                  </>
                ) : linkCopied ? (
                  <>
                    <CheckIcon className="h-4 w-4 mr-2" />
                    Web view ready
                  </>
                ) : (
                  <>
                    <DocumentDuplicateIcon className="mr-1.5 h-3.5 w-3.5 sm:mr-2 sm:h-4 sm:w-4" />
                    <span className="sm:hidden">Web</span>
                    <span className="hidden sm:inline">Web view</span>
                  </>
                )}
              </button>

              <button
              
                onClick={() => {
                  if (isProposalActionBusy) return;
                  setPdfModalAction("copyPdfLink");
                  setShowPDFOptionsModal(true);
                }}
                disabled={isProposalActionBusy || selectedArtworks.length === 0}
                className={`inline-flex shrink-0 items-center justify-center rounded-lg border px-2.5 py-1.5 text-xs transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:py-2 sm:text-sm ${
                  pdfLinkCopied
                    ? "text-green-600 bg-green-50 border-green-200 hover:bg-green-100"
                    : "text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100"
                }`}
              >
                {generatingPdfLink ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Generating...
                  </>
                ) : pdfLinkCopied ? (
                  <>
                    <CheckIcon className="h-4 w-4 mr-2" />
                    PDF link ready
                  </>
                ) : (
                  <>
                    <DocumentDuplicateIcon className="mr-1.5 h-3.5 w-3.5 sm:mr-2 sm:h-4 sm:w-4" />
                    <span className="sm:hidden">Copy link</span>
                    <span className="hidden sm:inline">Copy PDF link</span>
                  </>
                )}
              </button>

              <button
                onClick={() => {
                  
                  if (isProposalActionBusy) return;
                  setPdfModalAction("download");
                  setShowPDFOptionsModal(true);
                }}
                disabled={isProposalActionBusy || selectedArtworks.length === 0}
                className="inline-flex shrink-0 items-center rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs text-blue-600 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:py-2 sm:text-sm"
              >
                <ArrowDownTrayIcon className="mr-1.5 h-3.5 w-3.5 sm:mr-2 sm:h-4 sm:w-4" />
                <span>
                  {downloadingPDF
                    ? `${downloadDone}/${downloadTotal}…`
                    : (
                      <>
                        <span className="sm:hidden">Download</span>
                        <span className="hidden sm:inline">Download pdf</span>
                      </>
                    )}
                </span>
                
              </button>
              <button
                onClick={() => {
                  if (selectedArtworks.length === 0) {
                    alert("No artworks selected!");
                    return;
                  }
                  setPdfModalAction("view");
                  setShowPDFOptionsModal(true);
                }}
                className="inline-flex hidden items-center  px-4 py-2 text-sm text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 border border-purple-200 ml-2"
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                <span>View PDF ({selectedArtworks.length})</span>
              </button>
              <button
                onClick={() => {
                  if (selectedArtworks.length === 0) {
                    alert("No artworks selected!");
                    return;
                  }
                  setPdfModalAction("view");
                  setShowPDFOptionsModal(true);
                }}
                className="inline-flex hidden items-center  px-4 py-2 text-sm text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 border border-purple-200 ml-2"
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                <span>View PDF ({selectedArtworks.length})</span>
              </button>
            </div>
            )}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row">
          {/* Left */}

          <div className="w-full lg:w-[630px] lg:pr-6 space-y-6">

            {/* Send to */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Send to *
              </label>
              <div className="relative">
                <UserSearchSelect
                  value={recipient}
                  onChange={(user) => setRecipient(user)}
                  selectOnlyWithEmail={true}
                  includeInternalUsers={includeInternalUsers}
                />
              </div>
              {recipientError && (

                <div className="mt-2 text-xs text-red-600">
                  {recipientError}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CC (Optional)
              </label>
              <div className="relative">
                <div className="flex flex-wrap items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent bg-white">
                  {ccRecipients.map((r) => (
                    <span
                      key={r.id}
                      className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs border border-gray-200"
                    >
                      {r.label}
                      <button
                        type="button"
                        className="ml-1 text-gray-500"
                        onClick={() =>
                          setCcRecipients((prev) =>
                            prev.filter((x) => x.id !== r.id),
                          )
                        }
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void handleAddCcRecipient();
                      } else if (
                        (e.key === " " || e.key === ";") &&
                        emailRegex.test(ccInput.trim())
                      ) {
                        e.preventDefault();
                        void handleAddCcRecipient(ccInput.trim());
                      }
                    }}
                    onPaste={handleCcPaste}
                    onFocus={() => setShowCcContactSuggestions(true)}
                    onBlur={() =>
                      setTimeout(() => setShowCcContactSuggestions(false), 120)
                    }
                    placeholder={
                      ccRecipients.length === 0
                        ? "Type name or email for CC..."
                        : "Add another CC"
                    }
                    className="flex-1 min-w-[120px] border-none focus:outline-none focus:ring-0 py-1"
                  />
                </div>

                {showCcContactSuggestions && (
                  <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border rounded-md shadow max-h-64 overflow-auto">
                    {isSearchingCcContacts && (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        Searching…
                      </div>
                    )}
                    {!isSearchingCcContacts &&
                      (ccContactSuggestions?.length || 0) === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No results
                        </div>
                      )}
                    {ccContactSuggestions.map((c: any) => {
                      const displayName =
                        c.name || c.display_name || c.email || `ID ${c.id}`;
                      const email = c.email || c.Email1 || c.Email2 || "";
                      const line = email
                        ? `${displayName} : ${email}`
                        : displayName;
                      return (
                        <button
                          key={`cc-${c.id}`}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            addCcRecipientFromContact(c);
                          }}
                          onClick={() => addCcRecipientFromContact(c)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50"
                        >
                          <div className="text-sm text-gray-900">{line}</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {ccError && (
                <div className="mt-2 text-xs text-red-600">{ccError}</div>
              )}
            </div>

            {/* Custom Greeting */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Greeting</label>
              <input
                type="text"
                placeholder="Enter greeting (e.g., Dear, Hello, Hi)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={customGreeting}
                onChange={(e) => setCustomGreeting(e.target.value)}
              />
            </div> */}

            {/* Add Artwork(s) — hidden in list-mode (artworks are locked). */}
            {!hideArtworkPicker && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Add Artwork(s)
              </label>
              <div className="relative">
                <div className="flex flex-wrap items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent bg-white">
                  <input
                    value={artworkQuery}
                    onChange={(e) => setArtworkQuery(e.target.value)}
                    placeholder="Artwork name, Artist, Artwork ID..."
                    className="flex-1 min-w-[120px] border-none focus:outline-none focus:ring-0 py-1"
                  />
                  {(isSearchingArtworks ||
                    artworkSuggestions.length > 0 ||
                    filteredArtworkResults.length > 0) && (
                    <div className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow max-h-64 overflow-auto">
                      {isSearchingArtworks && (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          Searching…
                        </div>
                      )}
                      {artworkSuggestions.length > 0 &&
                        artworkSuggestions.map((a) => (
                          <button
                            key={`s-${a.id}`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleAddArtwork(a);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50"
                          >
                            <div className="flex items-center gap-3">
                              {a.image_url ? (
                                <img
                                  src={a.image_url}
                                  alt={a.title}
                                  className="w-10 h-10 object-cover rounded"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                                  No img
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="text-sm text-gray-900 truncate">
                                  {a.artist || "Artist"}, {a.title || "Artwork"}{" "}
                                  {a.IdName ? `• ${a.IdName}` : ""}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatArtworkPrice(
                                    a,
                                    hidePrices,
                                    selectedCurrency,
                                  ) || "Price on request"}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      {!isSearchingArtworks &&
                        artworkSuggestions.length === 0 &&
                        filteredArtworkResults.map((a) => (
                          <button
                            key={a.id}
                            onClick={() => handleAddArtwork(a)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50"
                          >
                            {a.artist || "Artist"}, {a.title || "Artwork"}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Artworks (carousel showing up to 3 at a time) */}
              {selectedArtworks.length > 0 && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-gray-700">
                      Selected artworks
                    </div>

                    {selectedArtworks.length > 0 && (
                      <div className="text-xs text-gray-500">
                        {carouselIndex + 1} / {selectedArtworks.length}

                      </div>
                    )}
                  </div>

                  <div
                    className="relative"
                    onKeyDown={(e) => {
                      if (e.key === "ArrowLeft")
                        setCarouselIndex((i) => Math.max(0, i - 1));

                      if (e.key === "ArrowRight")
                        setCarouselIndex((i) =>
                          Math.min(selectedArtworks.length - 1, i + 1),
                        );

                    }}
                    tabIndex={0}
                  >
                    {/* Track */}
                    <div className="overflow-hidden rounded-md">
                      <div
                        className="flex transition-transform duration-300 ease-out"

                        style={{
                          transform: `translateX(-${carouselIndex * 200}px)`,
                          // 192px card width + 8px (px-1 gap on both sides)
                        }}
                      >
                        {selectedArtworks.map((a) => (
                          <div key={a.id} className="w-48 shrink-0 px-1">
                            <div className="border rounded-md overflow-hidden bg-white flex flex-col">
                              {/* Image */}
                              <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                                {a.image_url ? (
                                  <img
                                    src={a.image_url}
                                    alt={a.title}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                                    No image
                                  </div>
                                )}
                              </div>

                              {/* Content */}
                              <div className="p-2 flex flex-col justify-between flex-1">
                                <div>
                                  <div className="text-xs font-semibold truncate max-w-[120px]">
                                    {a.title}
                                  </div>
                                  <div className="text-[10px] text-gray-600 truncate max-w-[120px]">
                                    {a.artist}
                                  </div>
                                  <div className="text-[10px] text-gray-400 truncate max-w-[120px]">
                                    {a.IdName || a.id}
                                  </div>
                                </div>

                                <div className="mt-2">
                                  <div className="text-[10px] text-gray-600">
                                    {/* {new Intl.NumberFormat("fr-FR", {
                                      style: "currency",
                                      currency: a.currency || "EUR",
                                    }).format(a.price || 0)} */}
                                    {(() => {
                                      const amount = a.price || 0;
                                      const currency = a.currency || "EUR";

                                      const formattedNumber =
                                        new Intl.NumberFormat("en-US", {
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 0,
                                        }).format(amount);

                                      return `${currency} ${formattedNumber}`;
                                    })()}
                                  </div>
                                  <button
                                    className="mt-1 text-[10px] text-red-600"
                                    onClick={() => handleRemoveArtwork(a.id)}
                                  >
                                    Remove
                                  </button>
                                </div>

                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Arrows */}

                    {selectedArtworks.length > 1 && (

                      <>
                        <button
                          type="button"
                          aria-label="Previous"
                          className="absolute left-1 top-1/2 -translate-y-1/2 bg-[#1D4ED8] shadow-sm rounded-full p-1.5 hover:brightness-110 disabled:opacity-40"
                          onClick={() =>
                            setCarouselIndex((i) => Math.max(0, i - 1))
                          }
                          disabled={carouselIndex === 0}
                        >
                          <ChevronLeftIcon className="h-4 w-4 text-white" />
                        </button>
                        <button
                          type="button"
                          aria-label="Next"
                          className="absolute right-1 top-1/2 -translate-y-1/2 bg-[#1D4ED8] shadow-sm rounded-full p-1.5 hover:brightness-110 disabled:opacity-40"

                          onClick={() =>
                            setCarouselIndex((i) =>
                              Math.min(selectedArtworks.length - 1, i + 1),
                            )
                          }

                          disabled={
                            carouselIndex === selectedArtworks.length - 1
                          }
                        >
                          <ChevronRightIcon className="h-4 w-4 text-white" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Suggested artworks — also gated on the picker so the
                whole "find more art" surface disappears in list-mode. */}
            {!hideArtworkPicker && sameArtistSuggestions.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-gray-700">
                    Suggested artworks{" "}
                    <span className="text-gray-400 text-xs">
                      (current artist)
                    </span>

                  </div>
                  {sameArtistSuggestions.length > 2 && !showAllSuggestions && (

                    <button
                      className="text-xs text-blue-600 hover:underline"
                      onClick={() => setShowAllSuggestions(true)}
                    >
                      View more
                    </button>
                  )}

                  {sameArtistSuggestions.length > 2 && showAllSuggestions && (

                    <button
                      className="text-xs text-blue-600 hover:underline"
                      onClick={() => setShowAllSuggestions(false)}
                    >
                      View less
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {(showAllSuggestions
                    ? sameArtistSuggestions
                    : sameArtistSuggestions.slice(0, 2)
                  ).map((a) => (
                    <button
                      key={a.id}
                      onClick={() => handleAddArtwork(a)}
                      className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs hover:bg-gray-200"
                    >
                      {a.artist || "Artist"}, {a.title || "Artwork"}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Message */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-medium text-gray-700">Message</div>
              </div>

              <div className="mb-3">
                <div className="mb-1.5 text-xs font-medium text-gray-600 sm:mb-1">
                  Template
                </div>
                <div className="flex flex-row flex-wrap items-center gap-4 sm:gap-6">
                  {(["1", "2"] as const).map((id) => (
                    <label
                      key={id}
                      className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-gray-700 sm:text-sm"
                    >
                      <input
                        type="radio"
                        name="template"
                        value={id}
                        className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4"
                        checked={selectedTemplate === id}
                        onChange={() => {
                          setSelectedTemplate(id);
                          if (id === "1" || id === "2") {
                            setMessageContent(
                              generateTemplateMessageById(id),
                            );
                          }
                        }}
                      />
                      <span>Template {id}</span>
                    </label>
                  ))}
                </div>

              </div>
            </div>
          </div>

          {/* Right Preview */}
          <div className="w-full lg:w-[890px] bg-[#DBEAFE] p-4 sm:p-6 lg:p-[30px] lg:border-l border-t lg:border-t-0 relative">
            <div className="flex items-center justify-end mb-3 gap-2">
              <button
                onClick={async () => {
                  const text = `${fullTextContent || messageContent}`;
                  const ok = await copyToClipboardSafe(text);
                  if (ok) {
                    setMessageCopied(true);
                    setTimeout(() => setMessageCopied(false), 1500);
                  } else {
                    // eslint-disable-next-line no-alert
                    alert(
                      "Clipboard blocked. You can copy from the message box.",
                    );
                  }
                }}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs border ${
                  messageCopied
                    ? "text-green-700 bg-green-50 border-green-200"
                    : "text-blue-700 bg-white/70 hover:bg-white border-blue-200"
                }`}
              >
                {messageCopied ? (
                  <CheckIcon className="h-3.5 w-3.5" />
                ) : (
                  <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                )}
                {messageCopied ? "Copied" : "Copy"}
              </button>
            </div>

            {/* Message card - read/edit modes */}
            {!isEditingPreview ? (
              <div className="bg-[#DBEAFE] p-4 text-[13px] text-gray-600 leading-relaxed">
                <div className="text-xs text-gray-600 mb-2 text-right">
                  {new Date().toLocaleDateString()}
                </div>
                <div
                  className="whitespace-pre-wrap nice-scrollbar overflow-auto"
                  style={{ height: `${messageBoxHeight}px` }}
                >
                  {/* {fullTextContent && fullTextContent.trim().length > 0 ? ( */}
                  {fullTextContent !== null ? (
                    (() => {
                      const linkPattern = /(https?:\/\/[^\s]+)/;
                      const match = fullTextContent.match(linkPattern);
                      if (match) {
                        const idx = fullTextContent.indexOf(match[0]);
                        const msg = fullTextContent.substring(0, idx).trim();
                        const link = fullTextContent.substring(idx).trim();
                        return (
                          <>
                            <div>{msg}</div>
                            <div className="mt-4 text-xs">
                              <a
                                href={link}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 break-all"
                              >
                                {link}
                              </a>
                            </div>
                          </>
                        );
                      }
                      return <>{fullTextContent}</>;
                    })()
                  ) : (
                    <>
                      <div className="text-gray-900 mb-3">{`${
                        customGreeting || "Dear"
                      } ${greetingName || "Client"},`}</div>
                      {messageContent}
                      {shareUrl && (

                        <div className="mt-4 text-xs">
                          <div className="mb-1 font-medium text-gray-600">
                            Web view
                          </div>
                          <a
                            href={shareUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 break-all"
                          >
                            {shareUrl}
                          </a>

                        </div>
                      )}
                      {pdfShareUrl && (
                        <div className="mt-4 text-xs">
                          <div className="mb-1 font-medium text-gray-600">
                            {pdfShareLinks.length > 1 ? "PDF links" : "PDF link"}
                          </div>
                          <div className="space-y-1">
                            {pdfShareLinks.map((link, index) => (
                              <a
                                key={`${link}-${index}`}
                                href={link}
                                target="_blank"
                                rel="noreferrer"
                                className="block text-blue-600 break-all"
                              >
                                {link}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                {/* Signature preview */}
                <div className="mt-4 bg-white rounded-md p-3 border border-blue-100">
                  <div className="text-xs text-gray-500 mb-2">
                    Signature preview
                  </div>
                  <div dangerouslySetInnerHTML={{ __html: signatureHtml }} />
                </div>
                {(shareUrl || pdfShareUrl) && (
                  <div className="mt-4 bg-white rounded-md p-3 border border-blue-100 space-y-3">
                    {shareUrl && (
                      <div className="text-xs">
                        <div className="mb-1 font-medium text-gray-600">
                          Web view
                        </div>
                        <a
                          href={shareUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 break-all"
                        >
                          {shareUrl}
                        </a>
                      </div>
                    )}
                    {pdfShareUrl && (
                      <div className="text-xs">
                        <div className="mb-1 font-medium text-gray-600">
                          {pdfShareLinks.length > 1 ? "PDF links" : "PDF link"}
                        </div>
                        <div className="space-y-1">
                          {pdfShareLinks.map((link, index) => (
                            <a
                              key={`${link}-${index}`}
                              href={link}
                              target="_blank"
                              rel="noreferrer"
                              className="block text-blue-600 break-all"
                            >
                              {link}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="mt-4">
                  <button
                    onClick={() => setIsEditingPreview(true)}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs text-blue-700 bg-transparent hover:bg-white/60"
                  >
                    Edit
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="bg-white p-4">
                  <div className="text-xs text-gray-600 mb-2 text-right">
                    {new Date().toLocaleDateString()}
                  </div>
                  <textarea
                    ref={editorRef}
                    className="w-full text-sm text-gray-900 resize-none outline-none nice-scrollbar rounded-none border-0 p-3"
                    style={{
                      height: `${messageBoxHeight}px`,
                      direction: "ltr",
                      textAlign: "left",
                    }}
                    // value={
                    //   fullTextContent ||
                    //   `${customGreeting || "Dear"} ${
                    //     greetingName || "Client"
                    //   },\n\n${messageContent}${
                    //     shareUrl ? `\n\n${shareUrl}` : ""
                    //   }`
                    // }
                    value={
                      fullTextContent !== null
                        ? fullTextContent
                        : `${customGreeting || "Dear"} ${
                            greetingName || "Client"
                          },\n\n${messageContent}${
                            shareUrl ? `\n\n${shareUrl}` : ""
                          }`
                    }
                    onChange={handleTextareaChange}
                    placeholder="Type your message here..."
                  />
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    onClick={() => {
                      setIsEditingPreview(false);
                      setMessageContent(
                        generateTemplateMessageById(selectedTemplate),
                      );
                    }}
                    className="px-3 py-1 rounded-full text-xs bg-white text-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const value = editorRef.current?.value || "";

                      const lines = value.split(/\r?\n/);
                      let startIdx = 0;
                      if (lines.length > 0 && /^\s*dear\b/i.test(lines[0])) {
                        startIdx = 1;
                        if (lines[1] !== undefined && lines[1].trim() === "")
                          startIdx = 2;
                      }

                      const withoutGreeting = lines
                        .slice(startIdx)
                        .join("\n")
                        .trim();

                      const linkPattern = /(https?:\/\/[^\s]+)/;
                      const match = withoutGreeting.match(linkPattern);
                      if (match) {
                        const idx = withoutGreeting.indexOf(match[0]);
                        const msg = withoutGreeting.substring(0, idx).trim();
                        const link = withoutGreeting.substring(idx).trim();
                        setMessageContent(msg);
                        setShareUrl(link);
                      } else {
                        setMessageContent(withoutGreeting);
                        setShareUrl("");
                      }
                      setFullTextContent(value);
                      setIsEditingPreview(false);

                      setTimeout(
                        () =>
                          editorRef.current && setCaretToEnd(editorRef.current),
                        0,
                      );
                    }}
                    className="px-3 py-1 rounded-full text-xs bg-blue-600 text-white"
                  >
                    Save
                  </button>
                </div>
              </>
            )}

            {/* Attachments — when `hideAttachmentPicker` is set the user can't
                add their own files (list-mode caller controls the contents),
                but we still surface any auto-generated attachments as a
                read-only chip so the user has visual confirmation that a
                PDF will go out with the email. */}
            {hideAttachmentPicker && attachments.length > 0 && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attachment
                </label>
                <div className="space-y-1">
                  {attachments.map((a, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 text-xs text-gray-700 border rounded px-2 py-1.5 bg-gray-50"
                    >
                      <span className="text-gray-400">📎</span>
                      <span className="truncate flex-1">{a.name}</span>
                      <span className="text-[10px] uppercase tracking-wide text-gray-400">
                        Auto-attached
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Attachments — picker hidden when the caller has prefilled
                a controlled set of files (list-mode external send). The
                user's files would otherwise leak into the proposal. */}
            {!hideAttachmentPicker && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attachments
              </label>
              <input
                type="file"
                multiple
                ref={fileInputRef}
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  const toBase64 = (file: File) =>
                    new Promise<string>((resolve, reject) => {

                      const reader = new FileReader();
                      reader.onload = () =>
                        resolve((reader.result as string).split(",")[1] || "");
                      reader.onerror = reject;
                      reader.readAsDataURL(file);
                    });
                  const newAtt: ProposalEmailAttachment[] = [];
                  for (const f of files) {
                    const b64 = await toBase64(f);
                    newAtt.push({
                      name: f.name,
                      content: b64,
                      source: "external",
                    });
                  }
                  setAttachments((prev) => [...prev, ...newAtt]);
                  if (files.length > 1) setAttachmentsExpanded(false);
                }}
                className="block w-full text-sm text-gray-700"
              />
              {attachments.length > 0 && (
                <div className="mt-2">
                  {attachments.length > 1 && !attachmentsExpanded ? (
                    <div className="flex items-center justify-between text-xs text-gray-700 border rounded px-2 py-1">
                      <span className="truncate">
                        {attachments.length} files attached
                      </span>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setAttachmentsExpanded(true)}
                          className="text-blue-600 hover:underline"
                        >
                          View
                        </button>
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="text-gray-600 hover:underline"
                        >
                          Add more
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {attachments.map((a, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between text-xs text-gray-700 border rounded px-2 py-1"
                        >
                          <span className="truncate mr-2">{a.name}</span>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="text-gray-600 hover:underline"
                            >
                              Add more
                            </button>
                            <button
                              onClick={() =>
                                setAttachments((prev) =>
                                  prev.filter((_, i) => i !== idx),
                                )
                              }
                              className="text-red-600 hover:underline"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                      {attachments.length > 1 && (
                        <div className="text-right mt-1">
                          <button
                            onClick={() => setAttachmentsExpanded(false)}
                            className="text-xs text-gray-600 hover:underline"
                          >
                            Hide
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            )}
            {/* Hide the "generate a proposal first" hint in list-mode —
                the modal auto-attaches the PDF (or sets a dashboard URL)
                as soon as a recipient is picked, so a manual generation
                step doesn't apply. */}
            {!hasPdfOrLink && !hideArtworkPicker && (
              <div className="mb-1 mt-3 text-xs text-red-600 text-center">
                Please generate a proposal by copying the link, or downloading
                the PDF before sending it as an attachment.
              </div>
            )}
            {!hasPdfOrLink && hideArtworkPicker && !listModePreparing && (
              <div className="mb-1 mt-3 text-xs text-gray-500 text-center">
                Pick a recipient to attach the list PDF.
              </div>
            )}
            <button
              className={`w-full py-3 rounded-md mt-4 text-white flex items-center justify-center gap-2 ${
                isSending || !hasPdfOrLink || listModePreparing
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
              disabled={isSending || !hasPdfOrLink || listModePreparing}
              onClick={async () => {
                let anySent = false;
                try {
                  if (!hasPdfOrLink) {
                    alert(
                      "Please attach a PDF or generate a proposal link before sending.",
                    );
                    return;
                  }
                  if (isSending) return;
                  // Accept a typed email in the input if no chip has been added
                  let sendList = recipients;
                  if (sendList.length === 0) {
                    const raw = (recipientInput || "").trim();
                    const direct = extractEmailFromLabel(raw) || raw;
                    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(direct);
                    if (isEmail) {
                      sendList = [{ id: direct, label: direct }];
                    } else {
                      // eslint-disable-next-line no-alert
                      alert("Please enter a valid email address.");
                      return;
                    }
                  }
                  setIsSending(true);
                  setSentFlag(false);
                  const { default: odooClient } =
                    await import("../services/odooClient");
                  const subject = buildEmailSubject();
                  const recipientName =
                    greetingName || getRecipientName(recipients[0]) || "Client";
                  // Default body — used for every recipient unless we route
                  // them differently below. List-mode (internal vs external)
                  // overrides this per-recipient to swap shareUrl/attachments
                  // without touching the rest of the send pipeline.
                  const bodyHtml = buildEmailHtml(
                    recipientName,
                    fullTextContent || messageContent,
                    shareUrl,
                    undefined,
                    signatureHtml,
                  );

                  // Per-recipient routing for list-mode. Default keeps the
                  // pre-loop `bodyHtml` and `attachments` exactly as before
                  // so contact-page / artwork-detail callers don't change.
                  const isInternalChip = (chip: RecipientChip): boolean => {
                    if (!isInternalRecipient) return false;
                    return isInternalRecipient({
                      email: extractEmailFromLabel(chip.label) || chip.id,
                      recipientLabel: chip.label,
                      recipientId: chip.id,
                    });
                  };
                  const buildBodyForRecipient = (chip: RecipientChip): string => {
                    if (isInternalChip(chip) && internalRecipientShareUrl) {
                      return buildEmailHtml(
                        getRecipientName(chip) || recipientName,
                        fullTextContent || messageContent,
                        internalRecipientShareUrl,
                        undefined,
                        signatureHtml,
                      );
                    }
                    return bodyHtml;
                  };
                  const buildAttachmentsForRecipient = (
                    chip: RecipientChip,
                  ): typeof attachments => {
                    // Internal recipients get the dashboard URL embedded in
                    // the body — they don't need (and shouldn't get) the
                    // generated PDF as an attachment too.
                    if (isInternalChip(chip)) return [];
                    return attachments;
                  };

                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  // const pendingCc = ccInput.trim();
                  let ccListForSend = [...ccRecipients];
                  const senderEmail = user?.email;
                  if (
                    senderEmail &&
                    !ccListForSend.some(
                      (cc) =>
                        (extractEmailFromLabel(cc.label) || cc.id) ===
                        senderEmail,
                    )
                  ) {
                    ccListForSend.push({ id: senderEmail, label: senderEmail });
                  }
                  const pendingCc = ccInput.trim();
                  if (
                    pendingCc &&
                    emailRegex.test(pendingCc) &&
                    !ccListForSend.some((cc) => cc.id === pendingCc)
                  ) {
                    ccListForSend.push({ id: pendingCc, label: pendingCc });
                  }
                  // if (
                  //   pendingCc &&
                  //   emailRegex.test(pendingCc) &&
                  //   !ccRecipients.some((cc) => cc.id === pendingCc)
                  // ) {
                  //   ccListForSend = [
                  //     ...ccRecipients,
                  //     { id: pendingCc, label: pendingCc },
                  //   ];
                  // }

                  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  const ccEmails = ccListForSend
                    .map((cc) => ({
                      email: extractEmailFromLabel(cc.label) || cc.id,
                      name: getRecipientName(cc),
                    }))
                    .filter((cc) => emailPattern.test(cc.email));


                  const backendUrl =
                    (odooClient as any)["backendURL"] ||
                    process.env.REACT_APP_BACKEND_URL ||
                    "";
                  const proposalSendLogClients = new Map<string, UserOption>();
                  const { syncProposalCrmPipeline } = await import(
                    "../utils/proposalCrmPipeline",
                  );
                  const artworksFullForLead =
                    await fetchFullArtworksForCrmPipeline();
                  const leadSyncCache = new Map<string, number | null>();
                  const fixedPipelineLeadId =
                    crmLeadIdProp != null &&
                    Number.isFinite(Number(crmLeadIdProp)) &&
                    Number(crmLeadIdProp) > 0
                      ? Number(crmLeadIdProp)
                      : null;

                  // Track leads that already got a `Proposal sync` price-history row
                  // during this single send action so multiple recipients that resolve
                  // to the same lead don't each append their own duplicate row.
                  const priceHistoryAppendedLeadIds = new Set<number>();
                  const appendSendPriceHistory = async (
                    leadId: number,
                  ): Promise<void> => {
                    if (priceHistoryAppendedLeadIds.has(leadId)) return;
                    priceHistoryAppendedLeadIds.add(leadId);
                    try {
                      const { sumExpectedRevenueForProposalArtworks, sumExpectedRevenueByCurrencyForProposalArtworks } =
                        await import("../utils/proposalCrmPipeline");
                      const expectedRevenue = sumExpectedRevenueForProposalArtworks(
                        artworksFullForLead,
                        selectedCurrency,
                      );
                      const revenueByCurrency =
                        sumExpectedRevenueByCurrencyForProposalArtworks(
                          artworksFullForLead,
                        );
                      const uid = odooClient.getOdooUserId();
                      if (!uid) return;
                      await odooClient.updateProposalOpportunityExpectedRevenue(
                        leadId,
                        expectedRevenue,
                        revenueByCurrency,
                        uid,
                      );
                    } catch (e) {
                      console.warn(
                        "Proposal send: failed to append price history row",
                        e,
                      );
                    }
                  };

                  const resolveLogLeadId = async (
                    chip: RecipientChip,
                    resolvedPartnerId?: number,
                  ): Promise<number | null> => {
                    let cid: number | null = null;
                    let cmodel: "crm.prospect" | "res.partner" = "res.partner";
                    if (/^\d+$/.test(chip.id)) {
                      cid = parseInt(chip.id, 10);
                      cmodel =
                        chip.contactModel === "crm.prospect"
                          ? "crm.prospect"
                          : "res.partner";
                    } else if (
                      chip.contactRecordId != null &&
                      Number.isFinite(Number(chip.contactRecordId))
                    ) {
                      cid = Number(chip.contactRecordId);
                      cmodel =
                        chip.contactModel === "crm.prospect"
                          ? "crm.prospect"
                          : "res.partner";
                    } else if (
                      resolvedPartnerId != null &&
                      Number.isFinite(resolvedPartnerId)
                    ) {
                      cid = resolvedPartnerId;
                      cmodel = "res.partner";
                    }
                    const key = cid != null ? `${cmodel}:${cid}` : null;
                    // Modal opened from the Offers card already knows the lead id and
                    // its PDF / link preview already ran the CRM sync with
                    // `skipPriceHistory: true`. Reuse that lead id, cache it against
                    // the recipient so the post-send attachment upload loop can find
                    // it, and explicitly append the single `Proposal sync` row for
                    // this send action.
                    if (fixedPipelineLeadId != null) {
                      if (key != null && !leadSyncCache.has(key)) {
                        leadSyncCache.set(key, fixedPipelineLeadId);
                      }
                      await appendSendPriceHistory(fixedPipelineLeadId);
                      return fixedPipelineLeadId;
                    }
                    if (key == null) return null;
                    if (leadSyncCache.has(key)) {
                      const cached = leadSyncCache.get(key)!;
                      if (cached != null) await appendSendPriceHistory(cached);
                      return cached;
                    }
                    // `skipPriceHistory: true` here + explicit
                    // `appendSendPriceHistory` below keeps exactly one history row
                    // per lead per send, even if the same lead is resolved for
                    // multiple recipients in this loop.
                    const lid = await syncProposalCrmPipeline(odooClient, {
                      clientId: cid!,
                      clientModel: cmodel,
                      artworksFull: artworksFullForLead,
                      selectedCurrency,
                      pdfFiles: [],
                      skipPriceHistory: true,
                    });
                    leadSyncCache.set(key, lid);
                    if (lid != null) await appendSendPriceHistory(lid);
                    return lid;
                  };

                  for (const r of sendList) {
                    const targetEmail = extractEmailFromLabel(r.label) || r.id;
                    if (/^\d+$/.test(r.id)) {
                      const legacyPartnerId = parseInt(r.id, 10);
                      if (targetEmail && emailPattern.test(targetEmail)) {
                        await odooClient.ensurePartnerPrimaryEmail(
                          legacyPartnerId,
                          targetEmail,
                        );
                        const logLeadId = await resolveLogLeadId(r);
                        await odooClient.sendEmailDirect(
                          String(targetEmail),
                          subject,
                          buildBodyForRecipient(r),
                          attachmentsForOdooEmail(buildAttachmentsForRecipient(r)),
                          {
                            recipientName: getRecipientName(r),
                            senderName: [user?.firstName, user?.lastName]
                              .filter(Boolean)
                              .join(" ")
                              .trim(),
                            ccEmails: ccEmails.length > 0 ? ccEmails : undefined,
                            ...(logLeadId != null ? { crmLeadId: logLeadId } : {}),
                          },
                        );

                        anySent = true;
                        {
                          const m =
                            r.contactModel === "crm.prospect"
                              ? "crm.prospect"
                              : "res.partner";
                          proposalSendLogClients.set(`${m}:${legacyPartnerId}`, {
                            id: legacyPartnerId,
                            name: getRecipientName(r),
                            _model: m,
                          });
                        }
                      } else {
                        console.warn(
                          "Skipping recipient: no valid email for contact ID",
                          legacyPartnerId,
                          r.label,
                        );
                      }
                      continue;
                    }
                    if (
                      targetEmail &&
                      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(targetEmail)
                    ) {
                      const displayName =
                        getRecipientName(r) || targetEmail.split("@")[0];
                      let targetPartnerId =
                        await odooClient.getOrCreatePartnerByEmail(
                          targetEmail,
                          displayName,
                        );
                      if (!targetPartnerId) {
                        targetPartnerId =
                          await odooClient.findPartnerIdByEmail(targetEmail);
                      }
                      if (targetPartnerId) {
                        await odooClient.ensurePartnerPrimaryEmail(
                          targetPartnerId,
                          targetEmail,
                        );

                        const logLeadId = await resolveLogLeadId(
                          r,
                          targetPartnerId,
                        );
                        await odooClient.sendEmailDirect(
                          String(targetEmail),
                          subject,
                          buildBodyForRecipient(r),
                          attachmentsForOdooEmail(
                            buildAttachmentsForRecipient(r),
                          ),
                          {
                            recipientName: getRecipientName(r),
                            senderName: [user?.firstName, user?.lastName]
                              .filter(Boolean)
                              .join(" ")
                              .trim(),

                            ccEmails:
                              ccEmails.length > 0 ? ccEmails : undefined,
                            ...(logLeadId != null ? { crmLeadId: logLeadId } : {}),
                          },
                        );

                        anySent = true;
                        if (
                          r.contactRecordId != null &&
                          Number.isFinite(Number(r.contactRecordId))
                        ) {
                          const m =
                            r.contactModel === "crm.prospect"
                              ? "crm.prospect"
                              : "res.partner";
                          proposalSendLogClients.set(
                            `${m}:${r.contactRecordId}`,
                            {
                              id: r.contactRecordId,
                              name: getRecipientName(r),
                              _model: m,
                            },
                          );
                        } else {
                          proposalSendLogClients.set(
                            `res.partner:${targetPartnerId}`,
                            {
                              id: targetPartnerId,
                              name: displayName,
                              _model: "res.partner",
                            },
                          );
                        }
                        continue;
                      }
                    }
                  }

                  if (anySent && proposalSendLogClients.size > 0) {
                    const externalOnly = attachments.filter(
                      (a) => a.source === "external",
                    );
                    const crmFilesFromExternal =
                      externalOnly.length > 0
                        ? externalOnly.map((a) => ({
                            blob: base64ToBlobForCrm(a.content, a.name),
                            fileName: a.name,
                          }))
                        : [];
                    if (crmFilesFromExternal.length > 0) {
                      // The CRM pipeline (lead + price history line) was already
                      // synced once per recipient inside `resolveLogLeadId`. Calling
                      // `ensureProposalCrmPipelineForClient` again here would re-run
                      // `syncProposalCrmPipeline` and append a second `Proposal sync`
                      // entry to `opportunity_price_history_ids` (visible as duplicate
                      // rows on the Offers Price History tab). Reuse the lead ids that
                      // were just resolved and upload the external attachments
                      // straight to each lead's chatter instead.
                      for (const [key] of proposalSendLogClients) {
                        const leadIdForUpload = leadSyncCache.get(key);
                        if (
                          leadIdForUpload == null ||
                          !Number.isFinite(Number(leadIdForUpload)) ||
                          Number(leadIdForUpload) <= 0
                        ) {
                          continue;
                        }
                        for (const f of crmFilesFromExternal) {
                          try {
                            await odooClient.uploadMailAttachmentToThread({
                              threadId: Number(leadIdForUpload),
                              file: f.blob,
                              fileName: f.fileName,
                            });
                          } catch (uploadErr) {
                            console.warn(
                              "Failed to upload proposal attachment to crm.lead",
                              leadIdForUpload,
                              uploadErr,
                            );
                          }
                        }
                      }
                    }
                    const artworksNote = buildArtworksForProposalNote();
                    const timelineLoggedLeadIds = new Set<number>();
                    const selectedArtworkIdsForCount = selectedArtworks.map(
                      (sa: any) => sa?.id,
                    );
                    for (const [key, c] of proposalSendLogClients) {
                      await postProposalClientNote(
                        "sendMail",
                        artworksNote,
                        c,
                        selectedArtworks,
                      );
                      // Proposal counter: one distinct client per iteration →
                      // one row per (seller, client, artwork). Multi-recipient
                      // sends naturally bump multiple client rows without
                      // duplicating artwork rows.
                      await odooClient.recordArtworkProposalAction(
                        selectedArtworkIdsForCount,
                        c,
                      );
                      const lidForTimeline = leadSyncCache.get(key);
                      if (
                        lidForTimeline != null &&
                        Number.isFinite(Number(lidForTimeline)) &&
                        Number(lidForTimeline) > 0 &&
                        !timelineLoggedLeadIds.has(Number(lidForTimeline))
                      ) {
                        timelineLoggedLeadIds.add(Number(lidForTimeline));
                        await appendOpportunityTimelineEntry(
                          "sendMail",
                          Number(lidForTimeline),
                        );
                      }
                    }
                  }

                  // ✅ LOG PDF SENT (ONCE PER SEND ACTION)
                  if (user?.id && anySent) {
                    fetch(`${backendUrl}/api/activity`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      credentials: "include",
                      body: JSON.stringify({
                        user_id: user.id,
                        user_name: `${user?.firstName} ${user?.lastName}`,
                        action: "send_pdf",
                        meta: {
                          source: "email",
                        },
                      }),
                    }).catch(() => {});
                  }


                  setSentFlag(true);
                  setTimeout(() => setSentFlag(false), 2500);
                  if (anySent) {
                    setTimeout(() => {
                      onClose();
                      setRecipient(null);
                      setRecipients([]);
                      setRecipientInput("");
                      setContactSuggestions([]);
                      setShowContactSuggestions(false);
                      setCcRecipients([]);
                      setCcInput("");
                      setCcContactSuggestions([]);
                      setShowCcContactSuggestions(false);
                      setAttachments([]);
                      setArtworkQuery("");

                      setRecipientError(null);
                      setCcError(null);
                      setMessageContent(generateTemplateMessageById("1"));
                      setFullTextContent("");
                      setGreetingName("");
                      setCustomGreeting("Dear");
                      setSignatureTemplate("sig1");
                      setShareUrl(null);
                      setPdfShareUrl(null);
                      setDownloadDone(0);
                      setDownloadTotal(0);
                      setDownloadingPDF(false);
                      setIsSending(false);
                      setIsPdfReady(false);
                      setMessageCopied(false);
                      setLinkCopied(false);
                      setGeneratingLink(false);
                      setPdfLinkCopied(false);
                      setGeneratingPdfLink(false);
                      setDownloadDone(0);
                      setDownloadTotal(0);
                    }, 50);
                  }
                } catch (e) {
                  // eslint-disable-next-line no-alert
                  alert("Failed to send message");
                } finally {
                  setIsSending(false);
                }
              }}
            >
              {(isSending || listModePreparing) && (
                <svg
                  className="animate-spin h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
              )}
              {isSending
                ? "Sending..."
                : listModePreparing
                  ? "Preparing…"
                  : sentFlag
                    ? "Sent ✓"
                    : "Send"}
            </button>
          </div>
        </div>
      </div>

      {/* {showPDFOptionsModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowPDFOptionsModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {pdfModalAction === "download"
                  ? "Download PDF Options"
                  : "Copy Link PDF Options"}
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Choose whether to include the price in the PDF:
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    if (pdfModalAction === "download") {
                      // handleDownloadProposalPDF(true);
                      setIncludePrice(true);
                      autoAttachProposalPDF(true, pdfMode!);
                    } else {
                      handleCopyProposalLink(true);
                    }
                    setShowPDFOptionsModal(false);
                  }}
                  className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 border-2 border-gray-300 rounded-lg transition-colors"
                >
                  <div className="font-medium text-gray-900">With Price</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Include price in{" "}
                    {selectedCurrency === "MultiDevices"
                      ? "Multi Device Currency"
                      : selectedCurrency}
                  </div>
                </button>
                <button
                  onClick={() => {
                    if (pdfModalAction === "download") {
                      // handleDownloadProposalPDF(false);
                      setIncludePrice(false);
                      autoAttachProposalPDF(false, pdfMode!);
                    } else {
                      handleCopyProposalLink(false);
                    }
                    setShowPDFOptionsModal(false);
                  }}
                  className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 border-2 border-gray-300 rounded-lg transition-colors"
                >
                  <div className="font-medium text-gray-900">Without Price</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Show "Price on Request" instead
                  </div>
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-6 mb-3 ">
                Choose PDF format:
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => setPdfMode("single")}
                  className={`w-full px-4 py-3 border-2 rounded-lg ${
                    pdfMode === "single"
                      ? "border-black bg-gray-100"
                      : "border-gray-300"
                  }`}
                >
                  Single PDF
                </button>

                <button
                  disabled={isSingleArtwork}
                  onClick={() => setPdfMode("multiple")}
                  className={`w-full px-4 py-3 border-2 rounded-lg ${
                    isSingleArtwork
                      ? "opacity-50 cursor-not-allowed"
                      : pdfMode === "multiple"
                        ? "border-black bg-gray-100"
                        : "border-gray-300"
                  }`}
                >
                  Multiple PDFs
                </button>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end">
              <button
                disabled={isConfirmDisabled}
                onClick={() => {
                  handleDownloadProposalPDF(includePrice!, pdfMode!);
                  autoAttachProposalPDF(includePrice!, pdfMode!);
                  setShowPDFOptionsModal(false);
                }}
                className={`px-4 py-2 rounded-lg ${
                  isConfirmDisabled
                    ? "bg-gray-300 text-gray-500"
                    : "bg-black text-white"
                }`}
              >
                Confirm
              </button>
              <button
                onClick={() => setShowPDFOptionsModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )} */}
      {showPDFOptionsModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowPDFOptionsModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* TITLE */}
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {pdfModalAction === "download"
                  ? "Download PDF Options"
                  : pdfModalAction === "view"
                    ? "View PDF Options"
                    : pdfModalAction === "copyPdfLink"
                      ? "Copy PDF Link Options"
                      : "Web View Options"}
              </h3>

              {/* <p className="text-sm text-gray-600 mb-2">Link to a Contact (optional)</p>
              <div className="mb-5">
                <UserSearchSelect
                  value={proposalPdfClient}
                  onChange={(u) => setProposalPdfClient(u)}
                  placeholder="Search contact to log on…"
                />
              </div>  */}

              {/* PRICE SECTION */}
              <p className="text-sm text-gray-600 mb-4">
                Choose whether to include the price in the PDF:
              </p>

              <div className="space-y-3">
                {/* WITH PRICE */}
                <button
                  disabled={isProposalActionBusy}
                  onClick={() => {
                    if (isProposalActionBusy) return;
                    if (
                      pdfModalAction === "download" ||
                      pdfModalAction === "view" ||
                      pdfModalAction === "copyPdfLink" ||
                      pdfModalAction === "webView"
                    ) {
                      setIncludePrice(true);
                    }
                  }}
                  className={`w-full px-4 py-3 text-left border-2 rounded-lg ${
                    includePrice === true
                      ? "border-black bg-gray-100"
                      : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                  }`}
                >
                  <div className="font-medium text-gray-900">With Price</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Include price in{" "}
                    {selectedCurrency === "MultiDevices"
                      ? "Multi Device Currency"
                      : selectedCurrency}
                  </div>
                </button>

                {/* WITHOUT PRICE */}
                <button
                  disabled={isProposalActionBusy}
                  onClick={() => {
                    if (isProposalActionBusy) return;
                    if (
                      pdfModalAction === "download" ||
                      pdfModalAction === "view" ||
                      pdfModalAction === "copyPdfLink" ||
                      pdfModalAction === "webView"
                    ) {
                      setIncludePrice(false);
                    }
                  }}
                  className={`w-full px-4 py-3 text-left border-2 rounded-lg ${
                    includePrice === false
                      ? "border-black bg-gray-100"
                      : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                  }`}
                >
                  <div className="font-medium text-gray-900">Without Price</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Show "Price on Request" instead
                  </div>
                </button>
              </div>

              {/* PDF MODE – FOR DOWNLOAD AND VIEW */}
              {(pdfModalAction === "download" ||
                pdfModalAction === "view" ||
                pdfModalAction === "copyPdfLink") && (
                <>
                  <p className="text-sm text-gray-600 mt-6 mb-3">
                    Choose PDF format:
                  </p>

                  <div className="space-y-3">
                    {/* SINGLE PDF */}
                    <button
                      disabled={isProposalActionBusy}
                      onClick={() => setPdfMode("multiple")}
                      className={`w-full px-4 py-3 text-left border-2 rounded-lg ${
                        isProposalActionBusy
                          ? "opacity-50 cursor-not-allowed border-gray-300"
                          : pdfMode === "multiple"
                            ? "border-black bg-gray-100"
                            : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                      }`}
                    >
                      <div className="font-medium">Single PDF</div>
                      <div className="text-sm text-gray-600">
                        All artworks in one PDF
                      </div>
                    </button>

                    {/* MULTIPLE PDFs */}
                    <button
                      disabled={isProposalActionBusy || isSingleArtwork}
                      onClick={() => setPdfMode("single")}
                      className={`w-full px-4 py-3 border-2 text-left rounded-lg ${
                        isProposalActionBusy || isSingleArtwork
                          ? "opacity-50 cursor-not-allowed border-gray-300"
                          : pdfMode === "single"
                            ? "border-black bg-gray-100"
                            : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                      }`}
                    >
                      <div className="font-medium">Multiple PDFs</div>
                      <div className="text-sm text-gray-600">
                        One PDF per artwork
                        {isSingleArtwork && (
                          <span className="block text-xs text-red-500 mt-1">
                            Not available for single artwork
                          </span>
                        )}
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* FOOTER */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex flex-col gap-3">
              {/* {(pdfModalAction === "download" || pdfModalAction === "view") && (
                <button
                  type="button"
                  onClick={() => {
                    setShowPDFOptionsModal(false);
                    handleDownloadProposalPDF(false, "single", "download", true);
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700 underline self-start"
                >
                  Generate test PDF (sample data)
                </button>
              )} */}
              <div className="flex justify-between">
                <button
                  disabled={isProposalActionBusy}
                  onClick={() => setShowPDFOptionsModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>

                {(pdfModalAction === "download" ||
                  pdfModalAction === "view" ||
                  pdfModalAction === "copyPdfLink" ||
                  pdfModalAction === "webView") && (
                  <button
                    disabled={!isConfirmEnabled || isProposalActionBusy}
                     onClick={async (e) => {
                      e.stopPropagation();
                      if (!isConfirmEnabled || isProposalActionBusy) return;
                       setShowPDFOptionsModal(false);
                      if (pdfModalAction === "webView") {
                        await handleCopyProposalLink(includePrice!);
                      } else if (pdfModalAction === "view") {
                        await handleDownloadProposalPDF(
                          includePrice!,
                          pdfMode!,
                          "view",
                        );
                        await syncInterestsAfterProposal(
                          selectedArtworks,
                          proposalPdfClient,
                        );
                      } else if (pdfModalAction === "copyPdfLink") {
                        await handleCopyProposalPdfLink(includePrice!, pdfMode!);
                      } else {
                        await handleDownloadProposalPDF(
                          includePrice!,
                          pdfMode!,
                          "download",
                        );
                        await autoAttachProposalPDF(includePrice!, pdfMode!);
                        await postProposalClientNote(
                          "download",
                          buildArtworksForProposalNote(),
                          undefined,
                          selectedArtworks,
                        );
                        // `handleDownloadProposalPDF("download")` runs
                        // `syncDownloadPdfsToCrmIfNeeded` → pipeline already
                        // appended an "Offer created" timeline row when a
                        // client was selected. Only fall back to the helper
                        // for the Offers-card flow (modal opened on an
                        // existing lead, no fresh client to sync) so a single
                        // click never produces two timeline entries.
                        if (!proposalPdfClient?.id) {
                          await appendOpportunityTimelineEntry("download");
                        }
                      }
                     
                    }}
                    className={`px-4 py-2 rounded-lg ${
                      !isConfirmEnabled
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                    }`}
                  >
                    Confirm
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
